import React, { useState, useEffect, useRef } from 'react';
import { UserPlus, MessageCircle, Users, Award, Link, Key, ChevronRight, X, Mail, Phone, Briefcase, CreditCard, Star, TrendingUp, Trash2, Edit2, MapPin, Send, RefreshCw, Upload, Image, Shield } from 'react-feather';
import AuthService from '../../services/authService';
import useTerritories from '../../hooks/useTerritories';
import AdminUserModal from './AdminUserModal';
import AgentDetailsModal from './AgentDetailsModal';
import TrackedLinksModal from './TrackedLinksModal';

// Tier styling + refund pricing. Mirrors backend utils/pointsConfig.js —
// keep in sync if point costs change.
const TIER_STYLES = {
  platinum: { bg: 'rgba(168, 85, 247, 0.15)', fg: '#7c3aed' },
  gold:     { bg: 'rgba(234, 179, 8, 0.15)',  fg: '#a16207' },
  silver:   { bg: 'rgba(148, 163, 184, 0.2)', fg: '#475569' },
  bronze:   { bg: 'rgba(180, 83, 9, 0.15)',   fg: '#92400e' },
  flex:     { bg: 'rgba(209, 213, 219, 0.25)', fg: '#4b5563' },
};
const tierStyle = (tier) => TIER_STYLES[(tier || '').toLowerCase()] || TIER_STYLES.flex;

const POINT_COSTS = { flex: 10, bronze: 10, silver: 25, gold: 45, platinum: 65 };
const STANDING_POINT_COSTS = { flex: 7, bronze: 7, silver: 12, gold: 20, platinum: 30 };
const STANDING_THRESHOLD_DAYS = 90;

// Refund equals what the agent was charged — which means STANDING users
// (unclaimed 90+ days at claim time) refund at the discounted rate.
const getPointCost = (tier, createdAt) => {
  const t = (tier || '').toLowerCase();
  if (createdAt) {
    const days = (Date.now() - new Date(createdAt)) / (1000 * 60 * 60 * 24);
    if (days >= STANDING_THRESHOLD_DAYS) return STANDING_POINT_COSTS[t] ?? 0;
  }
  return POINT_COSTS[t] ?? 0;
};

const CreateAgentForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    phone: '',
    agentInfo: {
      agency: '',
      licenseNumber: '',
      plan: 'Launch Advantage',
      aliasEmail: ''
    },
    // Which products this account gets. Defaults to Pro because that's what an
    // admin-created agent has always been. Sent straight through to
    // POST /admin/agents, which re-validates against the schema enum.
    entitlements: ['pro']
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [agents, setAgents] = useState([]);
  const [isFetchingAgents, setIsFetchingAgents] = useState(false);
  
  // Form visibility state
  const [showCreateForm, setShowCreateForm] = useState(false);
  
  // Tab state
  const [activeTab, setActiveTab] = useState('active');
  
  // Search and pagination for agents
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredAgents, setFilteredAgents] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  // 'all' | 'unassigned' | any market name from the Territory collection.
  // The market list is no longer hardcoded here — it comes from useTerritories(),
  // the same source the demo territory pickers read, so agentArea and
  // Demo.territory can't drift apart into different strings for the same place.
  // Markets are managed in the employee portal: Control Center -> Markets.
  const [territoryFilter, setTerritoryFilter] = useState('all');

  const {
    names: territoryNames,
    loading: territoriesLoading,
    error: territoriesError,
  } = useTerritories();
  const [totalPages, setTotalPages] = useState(1);
  const agentsPerPage = 12;
  
  // Agent Details Modal state
  const [showAgentDetailsModal, setShowAgentDetailsModal] = useState(false);
  const [selectedAgentForDetails, setSelectedAgentForDetails] = useState(null);
  const [agentDetailsUsers, setAgentDetailsUsers] = useState([]);
  const [isLoadingAgentDetails, setIsLoadingAgentDetails] = useState(false);

  // Sync MLS Listings state
  const [isSyncingListings, setIsSyncingListings] = useState(false);
  const [syncListingsResult, setSyncListingsResult] = useState(null);
  const [syncListingsError, setSyncListingsError] = useState('');
  
  // Password reset state
  const [resetModalOpen, setResetModalOpen] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [isResetting, setIsResetting] = useState(false);
  const [resetError, setResetError] = useState('');
  const [resetSuccess, setResetSuccess] = useState('');

  // Add Points state
  const [addPointsModalOpen, setAddPointsModalOpen] = useState(false);
  const [selectedAgentForPoints, setSelectedAgentForPoints] = useState(null);
  const [pointsToAdd, setPointsToAdd] = useState('');
  const [pointsReason, setPointsReason] = useState('');
  const [isAddingPoints, setIsAddingPoints] = useState(false);
  const [pointsError, setPointsError] = useState('');
  const [pointsSuccess, setPointsSuccess] = useState('');

  // Admin User Modal state
  const [adminUserModalOpen, setAdminUserModalOpen] = useState(false);
  const [selectedUserForModal, setSelectedUserForModal] = useState(null);

  // Referral management state
  const [referralModalOpen, setReferralModalOpen] = useState(false);
  const [selectedAgentForReferral, setSelectedAgentForReferral] = useState(null);
  const [referralLinks, setReferralLinks] = useState([]);
  const [newReferralData, setNewReferralData] = useState({
    campaignName: '',
    notes: ''
  });
  const [isCreatingReferral, setIsCreatingReferral] = useState(false);
  const [referralError, setReferralError] = useState('');
  const [referralSuccess, setReferralSuccess] = useState('');
  const [allReferralLinks, setAllReferralLinks] = useState([]);

  // Tracked links (campaign links with no agent) — separate from the per-agent
  // referral modal above, since these belong to no agent row.
  const [trackedLinksOpen, setTrackedLinksOpen] = useState(false);

  // Messaging state
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [selectedAgentForMessage, setSelectedAgentForMessage] = useState(null);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messageText, setMessageText] = useState('');
  const [isSending, setIsSending] = useState(false);
  const messagesEndRef = useRef(null);

  // Delete agent state
  const [isDeleting, setIsDeleting] = useState(false);

  // Refund user state
  const [refundingUserId, setRefundingUserId] = useState(null);
  const [refundSuccess, setRefundSuccess] = useState('');
  const [refundError, setRefundError] = useState('');
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [refundTargetUser, setRefundTargetUser] = useState(null);
  // Agent snapshot captured when the refund modal opens. The refund modal
  // outlives AgentDetailsModal (which nulls selectedAgentForDetails on close),
  // so the refund request must not read selectedAgentForDetails at submit time.
  const [refundAgent, setRefundAgent] = useState(null);
  const [refundReason, setRefundReason] = useState('');
  const [refundReasonSelected, setRefundReasonSelected] = useState('');

  // Pool management state
  const [allPools, setAllPools] = useState([]);
  const [agentPools, setAgentPools] = useState([]);
  const [isLoadingPools, setIsLoadingPools] = useState(false);
  const [isUpdatingPool, setIsUpdatingPool] = useState(false);
  const [poolSearchTerm, setPoolSearchTerm] = useState('');
  const [showPoolDropdown, setShowPoolDropdown] = useState(false);
  const [presetMissingPools, setPresetMissingPools] = useState([]);
  // User-defined pool presets from /agent-pool-presets (replaces hardcoded POOL_PRESETS)
  const [userPresets, setUserPresets] = useState([]);
  const [isApplyingPreset, setIsApplyingPreset] = useState(false);

  // Territory mapping for roster sort: { agentId: 'Denver' | 'Dallas/Fort Worth' | ... | 'Unassigned' }
  const [agentTerritoryMap, setAgentTerritoryMap] = useState({}); // Deprecated; kept to avoid breaking any stragglers
  // Inline-edit state for the Territory + Status columns (one row at a time)
  const [inlineEditAgentId, setInlineEditAgentId] = useState(null);
  const [inlineEditField, setInlineEditField] = useState(null); // 'area' | 'status'
  const [inlineEditValue, setInlineEditValue] = useState('');
  const [isSavingInline, setIsSavingInline] = useState(false);
  // Agent ids currently mid lock/unlock request, so the badge can't be
  // double-clicked into a race.
  const [lockBusyId, setLockBusyId] = useState(null);



  // Edit agent state
  const [showEditAgentModal, setShowEditAgentModal] = useState(false);
  const [editAgentData, setEditAgentData] = useState({
    name: '',
    email: '',
    phone: '',
    agency: '',
    licenseNumber: '',
    plan: '',
    aliasEmail: '',
    mlsCode: '',
    precautionMode: false,
    isDemoAccount: false,
    entitlements: ['pro']
  });
  const [isUpdatingAgent, setIsUpdatingAgent] = useState(false);
  const [editError, setEditError] = useState('');
  const [editSuccess, setEditSuccess] = useState('');

  // Ref for pool dropdown
  const poolDropdownRef = useRef(null);

  // Agent Invitations state
  const [agentInvitations, setAgentInvitations] = useState([]);
  const [isLoadingInvitations, setIsLoadingInvitations] = useState(false);
  const [resendingInviteId, setResendingInviteId] = useState(null);

  // QR Code upload state
  const [isUploadingQR, setIsUploadingQR] = useState(false);
  const [qrUploadError, setQrUploadError] = useState('');
  const [qrUploadSuccess, setQrUploadSuccess] = useState('');
  const qrFileInputRef = useRef(null);

  // Team Lead state
  const [teamMembers, setTeamMembers] = useState([]);
  const [isLoadingTeamMembers, setIsLoadingTeamMembers] = useState(false);
  const [isUpdatingTeamLead, setIsUpdatingTeamLead] = useState(false);
  const [teamLeadError, setTeamLeadError] = useState('');
  const [teamLeadSuccess, setTeamLeadSuccess] = useState('');

  const API_URL = 'https://back-end-x7tx.onrender.com/api';

  const exportAgentsCSV = () => {
    const esc = (v) => {
      const s = (v ?? '').toString();
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const rows = [
      ['Name', 'Phone', 'Brokerage'],
      ...agents.map(a => [a.name, a.phone, a.agentInfo?.agency].map(esc)),
    ];
    const csv = rows.map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `agents-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  // Entitlements are a multi-select, so they don't fit handleChange's
  // name/value shape. At least one must stay selected — an account with none
  // can log in and see nothing at all, since every portal gate rejects it.
  const toggleEntitlement = (key) => {
    setFormData((prev) => {
      const current = prev.entitlements || [];
      const next = current.includes(key)
        ? current.filter((e) => e !== key)
        : [...current, key];
      if (next.length === 0) return prev;
      return { ...prev, entitlements: next };
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'agency' || name === 'licenseNumber' || name === 'plan' || name === 'aliasEmail') {
      setFormData({
        ...formData,
        agentInfo: {
          ...formData.agentInfo,
          [name]: value
        }
      });
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
  };

  const fetchAgents = async () => {
    setIsFetchingAgents(true);
    try {
      const response = await fetch(`${API_URL}/admin/agents`, {
        headers: {
          'Authorization': `Bearer ${AuthService.getToken()}`
        }
      });
      const data = await response.json();
      const agentsList = data.data || [];
      setAgents(agentsList);
      // Apply the active status filter to the initial load using the real agentStatus
      // field (bug previously read agent.status which doesn't exist)
      const initialStatus = activeTab;
      const initial = agentsList.filter(a => initialStatus === 'all' || (a.agentStatus || 'active') === initialStatus);
      setFilteredAgents(initial);
      calculatePagination(initial);
    } catch (err) {
      console.error('Error fetching agents:', err);
    } finally {
      setIsFetchingAgents(false);
    }
  };

  // Apply search + territory + status filters together, refresh pagination.
  // Pass overrides so same-tick state updates don't lose.
  const applyFilters = (overrides = {}) => {
    const term = overrides.searchTerm !== undefined ? overrides.searchTerm : searchTerm;
    const terr = overrides.territoryFilter !== undefined ? overrides.territoryFilter : territoryFilter;
    const status = overrides.activeTab !== undefined ? overrides.activeTab : activeTab;
    const termLower = term.trim().toLowerCase();
    const filtered = agents.filter(agent => {
      // Status match — uses the real agentStatus field from the User model
      const agentStatus = agent.agentStatus || 'active';
      if (status !== 'all' && agentStatus !== status) return false;
      // Search match
      if (termLower) {
        const matches =
          agent.name?.toLowerCase().includes(termLower) ||
          agent.email?.toLowerCase().includes(termLower) ||
          (agent.agentInfo?.agency && agent.agentInfo.agency.toLowerCase().includes(termLower));
        if (!matches) return false;
      }
      // Territory match — uses the real agentArea field
      if (terr !== 'all') {
        const area = agent.agentArea || null;
        if (terr === 'unassigned') return !area;
        return area === terr;
      }
      return true;
    });
    setFilteredAgents(filtered);
    calculatePagination(filtered);
  };

  const handleSearch = (term) => {
    setSearchTerm(term);
    applyFilters({ searchTerm: term });
  };

  const handleTerritoryFilterChange = (terr) => {
    setTerritoryFilter(terr);
    applyFilters({ territoryFilter: terr });
  };

  const calculatePagination = (data) => {
    const total = Math.ceil(data.length / agentsPerPage);
    setTotalPages(total || 1);
    setCurrentPage(1);
  };

  const getCurrentPageAgents = () => {
    const startIndex = (currentPage - 1) * agentsPerPage;
    const endIndex = startIndex + agentsPerPage;
    return filteredAgents.slice(startIndex, endIndex);
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const fetchAllReferralLinks = async () => {
    try {
      const response = await fetch(`${API_URL}/referrals/admin/all`, {
        headers: {
          'Authorization': `Bearer ${AuthService.getToken()}`
        }
      });
      const data = await response.json();
      setAllReferralLinks(data || []);
    } catch (err) {
      console.error('Error fetching referral links:', err);
    }
  };

  const fetchAllPools = async () => {
    try {
      const response = await fetch(`${API_URL}/agent-pools`, {
        headers: {
          'Authorization': `Bearer ${AuthService.getToken()}`
        }
      });
      const data = await response.json();
      if (data.success) {
        setAllPools(data.data || []);
      }
    } catch (err) {
      console.error('Error fetching pools:', err);
    }
  };

  // Territory derivation removed — now uses agent.agentArea directly from the User model.

  const fetchAgentPools = async (agentId) => {
    setIsLoadingPools(true);
    try {
      const response = await fetch(`${API_URL}/agent-pools/agent/${agentId}`, {
        headers: {
          'Authorization': `Bearer ${AuthService.getToken()}`
        }
      });
      const data = await response.json();
      if (data.success) {
        setAgentPools(data.data || []);
      }
    } catch (err) {
      console.error('Error fetching agent pools:', err);
    } finally {
      setIsLoadingPools(false);
    }
  };

  const addAgentToPool = async (poolId) => {
    if (!selectedAgentForDetails) return;
    setIsUpdatingPool(true);
    try {
      const response = await fetch(`${API_URL}/agent-pools/${poolId}/agents`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${AuthService.getToken()}`
        },
        body: JSON.stringify({ agentId: selectedAgentForDetails._id })
      });
      const data = await response.json();
      if (data.success) {
        await fetchAgentPools(selectedAgentForDetails._id);
      }
    } catch (err) {
      console.error('Error adding agent to pool:', err);
    } finally {
      setIsUpdatingPool(false);
    }
  };

  const removeAgentFromPool = async (poolId) => {
    if (!selectedAgentForDetails) return;
    setIsUpdatingPool(true);
    try {
      const response = await fetch(`${API_URL}/agent-pools/${poolId}/agents/${selectedAgentForDetails._id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${AuthService.getToken()}`
        }
      });
      const data = await response.json();
      if (data.success) {
        await fetchAgentPools(selectedAgentForDetails._id);
      }
    } catch (err) {
      console.error('Error removing agent from pool:', err);
    } finally {
      setIsUpdatingPool(false);
    }
  };

  // Fetch user-defined pool presets (managed in Pool Management → Pool Presets)
  const fetchUserPresets = async () => {
    try {
      const response = await fetch(`${API_URL}/agent-pool-presets`, {
        headers: { Authorization: `Bearer ${AuthService.getToken()}` }
      });
      if (!response.ok) return;
      const data = await response.json();
      setUserPresets(data.data || []);
    } catch (err) {
      console.error('Error fetching user presets:', err);
    }
  };

  // Apply a user-defined preset: add every pool in the preset to the selected agent.
  // Preset shape: { _id, name, pools: [{ _id, name, serviceArea } | ObjectId] }
  // Matches by pool ID, so missing pools (deleted since preset creation) are surfaced.
  const applyPreset = async (preset) => {
    if (!selectedAgentForDetails || !preset) return;
    setIsApplyingPreset(true);
    setPresetMissingPools([]);

    const presetPoolIds = (preset.pools || []).map(p => (typeof p === 'object' ? p._id : p));
    const matched = [];
    const notMatched = [];

    presetPoolIds.forEach(poolId => {
      const found = allPools.find(pool => String(pool._id) === String(poolId));
      if (found) {
        const alreadyIn = agentPools.some(ap => String(ap._id) === String(found._id));
        if (!alreadyIn) matched.push(found);
      } else {
        // The pool was likely deleted after the preset was created.
        // Try to surface a name if we had one populated on the preset.
        const populated = (preset.pools || []).find(p => typeof p === 'object' && String(p._id) === String(poolId));
        notMatched.push(populated?.name || `Missing pool (${String(poolId).slice(-6)})`);
      }
    });

    if (notMatched.length > 0) {
      console.warn(`[Preset: ${preset.name}] Pools not found in current pool list:`, notMatched);
      setPresetMissingPools(notMatched);
    }

    // Add matched pools one at a time
    for (const pool of matched) {
      try {
        await fetch(`${API_URL}/agent-pools/${pool._id}/agents`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${AuthService.getToken()}`
          },
          body: JSON.stringify({ agentId: selectedAgentForDetails._id })
        });
      } catch (err) {
        console.error(`Error adding pool ${pool.name} from preset:`, err);
      }
    }

    if (matched.length > 0) {
      await fetchAgentPools(selectedAgentForDetails._id);
    }

    setIsApplyingPreset(false);
  };

  const removeAllPools = async () => {
    if (!selectedAgentForDetails || agentPools.length === 0) return;
    setIsApplyingPreset(true);
    for (const pool of agentPools) {
      try {
        await fetch(`${API_URL}/agent-pools/${pool._id}/agents/${selectedAgentForDetails._id}`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${AuthService.getToken()}` }
        });
      } catch (err) {
        console.error(`Error removing pool ${pool.name}:`, err);
      }
    }
    await fetchAgentPools(selectedAgentForDetails._id);
    setIsApplyingPreset(false);
  };

  const fetchTeamMembers = async (agentId) => {
    setIsLoadingTeamMembers(true);
    try {
      const response = await fetch(`${API_URL}/agents/${agentId}/team-members`, {
        headers: {
          'Authorization': `Bearer ${AuthService.getToken()}`
        }
      });
      const data = await response.json();
      if (data.success) {
        setTeamMembers(data.data || []);
      }
    } catch (err) {
      console.error('Error fetching team members:', err);
      setTeamMembers([]);
    } finally {
      setIsLoadingTeamMembers(false);
    }
  };

  const assignTeamLead = async (agentId, teamLeadId) => {
    setIsUpdatingTeamLead(true);
    setTeamLeadError('');
    setTeamLeadSuccess('');
    try {
      const response = await fetch(`${API_URL}/agents/${agentId}/team-lead`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${AuthService.getToken()}`
        },
        body: JSON.stringify({ teamLeadId: teamLeadId || null })
      });
      const data = await response.json();
      if (data.success) {
        setTeamLeadSuccess(teamLeadId ? 'Team lead assigned successfully' : 'Team lead removed successfully');
        // Update the selected agent's data
        setSelectedAgentForDetails(prev => ({
          ...prev,
          teamLead: teamLeadId ? agents.find(a => a._id === teamLeadId) : null
        }));
        // Refresh agents list to update any team member counts
        fetchAgents();
        setTimeout(() => setTeamLeadSuccess(''), 3000);
      } else {
        setTeamLeadError(data.error || 'Failed to update team lead');
      }
    } catch (err) {
      console.error('Error assigning team lead:', err);
      setTeamLeadError('Error updating team lead');
    } finally {
      setIsUpdatingTeamLead(false);
    }
  };

  // Helper to check if an agent is a team lead (has team members)
  const getTeamMemberCount = (agentId) => {
    return agents.filter(a => a.teamLead === agentId || a.teamLead?._id === agentId).length;
  };

  useEffect(() => {
    fetchAgents();
    fetchAllReferralLinks();
    fetchAllPools();
    fetchUserPresets();
  }, []);

  // Re-apply filters when the status tab changes
  useEffect(() => {
    applyFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // Click outside handler for pool dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (poolDropdownRef.current && !poolDropdownRef.current.contains(event.target)) {
        setShowPoolDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    setSuccess('');
  
    try {
      const response = await fetch(`${API_URL}/admin/agents`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${AuthService.getToken()}`
        },
        body: JSON.stringify(formData)
      });
  
      const data = await response.json();

      if (data.success) {
        setSuccess('Agent account created successfully!');
        setFormData({
          name: '',
          email: '',
          password: '',
          phone: '',
          agentInfo: {
            agency: '',
            licenseNumber: '',
            plan: 'Launch Advantage',
            aliasEmail: ''
          },
          entitlements: ['pro']
        });
        setShowCreateForm(false);
        fetchAgents();
      } else {
        setError(data.error || 'Failed to create agent account');
      }
    } catch (err) {
      setError('Error creating agent account: ' + err.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Password Reset Functions
  const openResetModal = (agent) => {
    setSelectedAgent(agent);
    setResetModalOpen(true);
    setNewPassword('');
    setResetError('');
    setResetSuccess('');
  };

  const closeResetModal = () => {
    setResetModalOpen(false);
    setSelectedAgent(null);
    setNewPassword('');
    setResetError('');
    setResetSuccess('');
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    setIsResetting(true);
    setResetError('');
    setResetSuccess('');

    if (newPassword.length < 5) {
      setResetError('Password must be at least 5 characters');
      setIsResetting(false);
      return;
    }

    try {
      const response = await fetch(`${API_URL}/admin/agents/${selectedAgent._id}/reset-password`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${AuthService.getToken()}`
        },
        body: JSON.stringify({ newPassword })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to reset password');
      }

      setResetSuccess(`Password successfully reset for ${selectedAgent.name}`);
      setTimeout(() => closeResetModal(), 2000);

    } catch (error) {
      setResetError(error.message || 'An error occurred while resetting password');
    } finally {
      setIsResetting(false);
    }
  };

  // Sync MLS Listings for a single agent
  const handleSyncAgentListings = async (agent) => {
    if (!agent || !agent._id) return;

    const confirmed = window.confirm(
      `Sync MLS listings for ${agent.name}?\n\n` +
      `This searches the MLS by their email and phone for active listings, ` +
      `imports them into our database, and links them to this agent.\n\n` +
      `This may take 30-90 seconds depending on how many listings they have.`
    );
    if (!confirmed) return;

    setIsSyncingListings(true);
    setSyncListingsResult(null);
    setSyncListingsError('');

    try {
      const response = await fetch(`${API_URL}/mls/sync/agent/${agent._id}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${AuthService.getToken()}`
        }
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Failed to sync agent listings');
      }

      setSyncListingsResult(data);
    } catch (error) {
      setSyncListingsError(error.message || 'An error occurred while syncing listings');
    } finally {
      setIsSyncingListings(false);
    }
  };

  // Add Points Functions
  const openAddPointsModal = (agent) => {
    setSelectedAgentForPoints(agent);
    setAddPointsModalOpen(true);
    setPointsToAdd('');
    setPointsReason('');
    setPointsError('');
    setPointsSuccess('');
  };

  const closeAddPointsModal = () => {
    setAddPointsModalOpen(false);
    setSelectedAgentForPoints(null);
    setPointsToAdd('');
    setPointsReason('');
    setPointsError('');
    setPointsSuccess('');
  };

  const handleAddPoints = async (e) => {
    e.preventDefault();
    setIsAddingPoints(true);
    setPointsError('');
    setPointsSuccess('');

    const points = parseInt(pointsToAdd);
    if (!points || points <= 0) {
      setPointsError('Please enter a valid positive number');
      setIsAddingPoints(false);
      return;
    }

    try {
      const response = await fetch(`${API_URL}/admin/agents/${selectedAgentForPoints._id}/add-points`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${AuthService.getToken()}`
        },
        body: JSON.stringify({ 
          pointsToAdd: points,
          reason: pointsReason || 'Admin added points'
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to add points');
      }

      setPointsSuccess(`Successfully added ${points} points to ${selectedAgentForPoints.name}`);
      fetchAgents();
      
      setTimeout(() => closeAddPointsModal(), 2000);

    } catch (error) {
      setPointsError(error.message || 'An error occurred while adding points');
    } finally {
      setIsAddingPoints(false);
    }
  };

  // Agent Details Modal Functions
  const openAgentDetailsModal = (agent) => {
    // AgentDetailsModal fetches its own pools/users/invitations/team/activity.
    setSelectedAgentForDetails(agent);
    setShowAgentDetailsModal(true);
  };

  const closeAgentDetailsModal = () => {
    setShowAgentDetailsModal(false);
    setSelectedAgentForDetails(null);
  };

  // Fetch agent's invitations
  const fetchAgentInvitations = async (agentId) => {
    setIsLoadingInvitations(true);
    try {
      const response = await fetch(`${API_URL}/admin/agents/${agentId}/invitations`, {
        headers: {
          'Authorization': `Bearer ${AuthService.getToken()}`
        }
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setAgentInvitations(data.data || []);
      }
    } catch (error) {
      console.error('Error fetching agent invitations:', error);
    } finally {
      setIsLoadingInvitations(false);
    }
  };

  // Resend an invitation (creates a new one with same data)
  const resendInvitation = async (invitation) => {
    setResendingInviteId(invitation._id);
    try {
      const response = await fetch(`${API_URL}/admin/invitations/${invitation._id}/resend`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${AuthService.getToken()}`
        }
      });

      const data = await response.json();

      if (response.ok && data.success) {
        // Refresh invitations list
        fetchAgentInvitations(selectedAgentForDetails._id);
        setSuccess(`Invitation resent to ${invitation.recipientName || invitation.recipientEmail}`);
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(data.error || 'Failed to resend invitation');
        setTimeout(() => setError(''), 3000);
      }
    } catch (error) {
      console.error('Error resending invitation:', error);
      setError('Failed to resend invitation');
      setTimeout(() => setError(''), 3000);
    } finally {
      setResendingInviteId(null);
    }
  };

  // QR Code upload function
  const handleQRCodeUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setQrUploadError('Please select an image file');
      setTimeout(() => setQrUploadError(''), 3000);
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setQrUploadError('Image must be less than 5MB');
      setTimeout(() => setQrUploadError(''), 3000);
      return;
    }

    setIsUploadingQR(true);
    setQrUploadError('');
    setQrUploadSuccess('');

    try {
      // Upload directly to Cloudinary (client-side)
      const formData = new FormData();
      formData.append('file', file);
      formData.append('upload_preset', 'lauft_profiles'); // Using same preset as profile images
      formData.append('folder', 'lauft/agent-qr-codes');

      console.log('Uploading to Cloudinary...');

      const cloudinaryResponse = await fetch(
        'https://api.cloudinary.com/v1_1/dckv4itgt/image/upload',
        {
          method: 'POST',
          body: formData
        }
      );

      if (!cloudinaryResponse.ok) {
        throw new Error('Failed to upload image to Cloudinary');
      }

      const cloudinaryData = await cloudinaryResponse.json();
      const imageUrl = cloudinaryData.secure_url;

      console.log('Image uploaded to Cloudinary:', imageUrl);

      // Now send the URL to backend
      const response = await fetch(`${API_URL}/admin/agents/${selectedAgentForDetails._id}/qr-code`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${AuthService.getToken()}`
        },
        body: JSON.stringify({ qrCodeUrl: imageUrl })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setQrUploadSuccess('QR code uploaded successfully!');
        // Update the selected agent's data
        setSelectedAgentForDetails({
          ...selectedAgentForDetails,
          agentInfo: {
            ...selectedAgentForDetails.agentInfo,
            qrCodeUrl: data.data.qrCodeUrl
          }
        });
        // Refresh agents list
        fetchAgents();
        setTimeout(() => setQrUploadSuccess(''), 3000);
      } else {
        setQrUploadError(data.error || 'Failed to update QR code');
        setTimeout(() => setQrUploadError(''), 5000);
      }
    } catch (error) {
      console.error('Error uploading QR code:', error);
      setQrUploadError(error.message || 'Failed to upload QR code');
      setTimeout(() => setQrUploadError(''), 5000);
    } finally {
      setIsUploadingQR(false);
      // Reset file input
      if (qrFileInputRef.current) {
        qrFileInputRef.current.value = '';
      }
    }
  };

  // Delete QR Code
  const handleDeleteQRCode = async () => {
    if (!window.confirm('Are you sure you want to delete this QR code?')) return;

    setIsUploadingQR(true);
    setQrUploadError('');

    try {
      const response = await fetch(`${API_URL}/admin/agents/${selectedAgentForDetails._id}/qr-code`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${AuthService.getToken()}`
        }
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setQrUploadSuccess('QR code deleted successfully!');
        // Update the selected agent's data
        setSelectedAgentForDetails({
          ...selectedAgentForDetails,
          agentInfo: {
            ...selectedAgentForDetails.agentInfo,
            qrCodeUrl: null
          }
        });
        // Refresh agents list
        fetchAgents();
        setTimeout(() => setQrUploadSuccess(''), 3000);
      } else {
        setQrUploadError(data.error || 'Failed to delete QR code');
        setTimeout(() => setQrUploadError(''), 5000);
      }
    } catch (error) {
      console.error('Error deleting QR code:', error);
      setQrUploadError('Failed to delete QR code');
      setTimeout(() => setQrUploadError(''), 5000);
    } finally {
      setIsUploadingQR(false);
    }
  };

  // Delete Agent Function
  const handleDeleteAgent = async (agent) => {
    const confirmDelete = window.confirm(
      `Are you sure you want to delete ${agent.name}'s agent account?\n\nThis action cannot be undone and will:\n• Remove the agent from the system\n• Unassign all their users\n• Delete their referral links`
    );

    if (!confirmDelete) return;

    setIsDeleting(true);
    setError('');

    try {
      const response = await fetch(`${API_URL}/admin/agents/${agent._id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${AuthService.getToken()}`
        }
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSuccess(`Successfully deleted agent: ${agent.name}`);
        closeAgentDetailsModal();
        fetchAgents();
        fetchAllReferralLinks();
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(data.error || 'Failed to delete agent');
        setTimeout(() => setError(''), 5000);
      }
    } catch (err) {
      setError('Error deleting agent: ' + err.message);
      setTimeout(() => setError(''), 5000);
    } finally {
      setIsDeleting(false);
    }
  };

  // Refund User Function - opens modal
  // `agent` is passed explicitly when available; otherwise we snapshot the
  // currently open agent. Either way we hold our own reference from here on.
  const handleRefundUser = (user, agent) => {
    const owner = agent || selectedAgentForDetails;
    if (!user?._id || !owner?._id) {
      setRefundError('Could not open refund — missing user or agent.');
      return;
    }
    setRefundTargetUser(user);
    setRefundAgent(owner);
    setRefundReason('');
    setRefundReasonSelected('');
    setRefundError('');
    setShowRefundModal(true);
  };

  const closeRefundModal = () => {
    setShowRefundModal(false);
    setRefundTargetUser(null);
    setRefundAgent(null);
    setRefundReason('');
    setRefundReasonSelected('');
  };

  const confirmRefundUser = async () => {
    if (!refundTargetUser?._id) return;
    if (!refundAgent?._id) {
      setRefundError('Agent context was lost. Close this and reopen the agent, then retry.');
      return;
    }

    setRefundingUserId(refundTargetUser._id);
    setRefundError('');
    setRefundSuccess('');

    try {
      const response = await fetch(`${API_URL}/admin/agents/${refundAgent._id}/refund-user`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${AuthService.getToken()}`
        },
        body: JSON.stringify({ 
          userId: refundTargetUser._id,
          reason: (refundReasonSelected === 'custom' ? refundReason.trim() : refundReasonSelected) || undefined
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        const refundedPoints = data?.data?.refundedPoints ?? getPointCost(refundTargetUser.leadTier, refundTargetUser.createdAt);
        setRefundSuccess(`Refunded ${refundTargetUser.name} — ${refundedPoints} points returned to ${refundAgent.name}`);
        setAgentDetailsUsers(prev => prev.filter(u => u._id !== refundTargetUser._id));
        fetchAgents();
        closeRefundModal();
        setTimeout(() => setRefundSuccess(''), 4000);
      } else {
        setRefundError(data.message || 'Failed to refund user');
      }
    } catch (err) {
      setRefundError('Error refunding user: ' + err.message);
    } finally {
      setRefundingUserId(null);
    }
  };

  // Edit Agent Functions
  // Same one-must-remain rule as the create form. An account with no
  // entitlements can log in and then be rejected by every portal gate.
  const toggleEditEntitlement = (key) => {
    setEditAgentData((prev) => {
      const current = prev.entitlements || [];
      const next = current.includes(key)
        ? current.filter((e) => e !== key)
        : [...current, key];
      if (next.length === 0) return prev;
      return { ...prev, entitlements: next };
    });
  };

  const openEditAgentModal = (agent) => {
    setEditAgentData({
      name: agent.name || '',
      email: agent.email || '',
      phone: agent.phone || '',
      agency: agent.agentInfo?.agency || '',
      licenseNumber: agent.agentInfo?.licenseNumber || '',
      plan: agent.agentInfo?.plan || 'Launch Advantage',
      aliasEmail: agent.agentInfo?.aliasEmail || '',
      mlsCode: agent.agentInfo?.mlsCode || '',
      precautionMode: agent.agentInfo?.precautionMode === true,
      isDemoAccount: agent.agentInfo?.isDemoAccount === true,
      // Legacy docs written before the field existed read as undefined. Show
      // them as Pro — that's what the backfill set every existing agent to,
      // and it's what they actually have.
      entitlements: Array.isArray(agent.entitlements) && agent.entitlements.length
        ? agent.entitlements
        : ['pro']
    });
    setEditError('');
    setEditSuccess('');
    setShowEditAgentModal(true);
  };

  const closeEditAgentModal = () => {
    setShowEditAgentModal(false);
    setEditAgentData({
      name: '',
      email: '',
      phone: '',
      agency: '',
      licenseNumber: '',
      plan: '',
      aliasEmail: '',
      mlsCode: '',
      precautionMode: false,
      isDemoAccount: false
    });
    setEditError('');
    setEditSuccess('');
  };

  const handleEditAgentChange = (e) => {
    const { name, value } = e.target;
    setEditAgentData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Precaution mode toggle — saves instantly via the dedicated admin endpoint.
  // Independent of "Save Changes"; flipping the switch fires the API call right
  // away. On success we update local state (so the toggle stays where the admin
  // put it) and refresh the agent list. On failure we revert and show an error.
  const handlePrecautionModeToggle = async () => {
    if (!selectedAgentForDetails?._id) return;

    const nextValue = !editAgentData.precautionMode;
    // Optimistically update the toggle so the admin gets instant feedback.
    setEditAgentData(prev => ({ ...prev, precautionMode: nextValue }));
    setEditError('');

    try {
      const response = await fetch(
        `${API_URL}/agents/${selectedAgentForDetails._id}/precaution-mode`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${AuthService.getToken()}`
          },
          body: JSON.stringify({ enabled: nextValue })
        }
      );

      const data = await response.json();

      if (response.ok && data.success) {
        // Refresh the roster so the agent's stored value reflects the change.
        fetchAgents();
        // Keep selectedAgentForDetails in sync too, since some flows reuse it.
        setSelectedAgentForDetails(prev => prev ? {
          ...prev,
          agentInfo: {
            ...(prev.agentInfo || {}),
            precautionMode: nextValue
          }
        } : prev);
      } else {
        // Revert on failure.
        setEditAgentData(prev => ({ ...prev, precautionMode: !nextValue }));
        setEditError(data.message || 'Failed to update precaution mode');
      }
    } catch (err) {
      // Revert on failure.
      setEditAgentData(prev => ({ ...prev, precautionMode: !nextValue }));
      setEditError('Error updating precaution mode: ' + err.message);
    }
  };

  // Demo account toggle — same pattern as precaution mode. Saves instantly
  // via a dedicated admin endpoint. Demo accounts see the full lead pool
  // (claimed + unclaimed) on Available Leads but can't actually claim.
  const handleDemoAccountToggle = async () => {
    if (!selectedAgentForDetails?._id) return;

    const nextValue = !editAgentData.isDemoAccount;
    // Optimistically update the toggle so the admin gets instant feedback.
    setEditAgentData(prev => ({ ...prev, isDemoAccount: nextValue }));
    setEditError('');

    try {
      const response = await fetch(
        `${API_URL}/agents/${selectedAgentForDetails._id}/demo-account`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${AuthService.getToken()}`
          },
          body: JSON.stringify({ enabled: nextValue })
        }
      );

      const data = await response.json();

      if (response.ok && data.success) {
        fetchAgents();
        setSelectedAgentForDetails(prev => prev ? {
          ...prev,
          agentInfo: {
            ...(prev.agentInfo || {}),
            isDemoAccount: nextValue
          }
        } : prev);
      } else {
        // Revert on failure.
        setEditAgentData(prev => ({ ...prev, isDemoAccount: !nextValue }));
        setEditError(data.message || 'Failed to update demo account status');
      }
    } catch (err) {
      // Revert on failure.
      setEditAgentData(prev => ({ ...prev, isDemoAccount: !nextValue }));
      setEditError('Error updating demo account status: ' + err.message);
    }
  };

  // ============================================
  // INLINE EDIT (Territory + Status columns in the agent roster)
  // ============================================
  // Format any date value to yyyy-mm-dd (local) for <input type="date">.
  const toDateInputValue = (d) => {
    const dt = new Date(d);
    if (isNaN(dt.getTime())) return '';
    const yyyy = dt.getFullYear();
    const mm = String(dt.getMonth() + 1).padStart(2, '0');
    const dd = String(dt.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  };

  const openInlineEdit = (agent, field) => {
    setInlineEditAgentId(agent._id);
    setInlineEditField(field);
    if (field === 'area') {
      setInlineEditValue(agent.agentArea || '');
    } else if (field === 'status') {
      setInlineEditValue(agent.agentStatus || 'active');
    } else if (field === 'pauseEnd') {
      // Prefill with the existing pause end date (as yyyy-mm-dd for the input).
      setInlineEditValue(agent.pauseEndsAt ? toDateInputValue(agent.pauseEndsAt) : '');
    }
  };

  const closeInlineEdit = () => {
    setInlineEditAgentId(null);
    setInlineEditField(null);
    setInlineEditValue('');
    setIsSavingInline(false);
  };

  /**
   * Is this agent still walled out of the portal?
   *
   * "Locked" isn't a flag on the account — it's the `onboarding_call` step being
   * unfinished. Same tell the backend uses.
   *
   * `generatedAt` guards legacy agents: anyone created before onboarding existed
   * has no agentOnboarding at all and has been using the portal for months, so
   * they must never render as locked.
   */
  const isAgentLocked = (agent) => {
    const ob = agent.agentOnboarding;
    if (!ob || !ob.generatedAt) return false;
    return !(ob.completedSteps || []).includes('onboarding_call');
  };

  /**
   * Lift (or reinstate) the portal lock. This is the control used live on the
   * onboarding call.
   */
  const toggleAgentLock = async (agent, locked) => {
    if (lockBusyId) return;

    if (!locked) {
      const ok = window.confirm(
        `Give ${agent.name} full access to the portal?\n\n` +
        `Do this once you've finished their onboarding call.`
      );
      if (!ok) return;
    }

    setLockBusyId(agent._id);

    try {
      const response = await fetch(`${API_URL}/admin/agents/${agent._id}/onboarding/lock`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${AuthService.getToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ locked })
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Could not update access');
      }

      // Patch the row in place. Refetching the whole list would reset the admin's
      // scroll position and filters mid-call, which is exactly the wrong moment.
      setAgents(prev => prev.map(a =>
        a._id === agent._id
          ? {
              ...a,
              agentOnboarding: {
                ...(a.agentOnboarding || {}),
                completedSteps: data.data.completedSteps,
                status: data.data.status
              }
            }
          : a
      ));
    } catch (err) {
      console.error('Error toggling agent lock:', err);
      alert(err.message || 'Could not update access');
    } finally {
      setLockBusyId(null);
    }
  };

  const handleInlineEditSave = async (agent) => {
    if (!agent || !inlineEditField) return;
    setIsSavingInline(true);
    try {
      const body = inlineEditField === 'area'
        ? { agentArea: inlineEditValue || null }
        : inlineEditField === 'pauseEnd'
          // Send the selected day at local end-of-day so it's strictly in the
          // future (the backend rejects past dates). Empty clears it.
          ? { pauseEndsAt: inlineEditValue ? new Date(`${inlineEditValue}T23:59:59`).toISOString() : null }
          : { agentStatus: inlineEditValue || 'active' };
      const response = await fetch(`${API_URL}/users/${agent._id}/agent-fields`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${AuthService.getToken()}`
        },
        body: JSON.stringify(body)
      });
      const data = await response.json();
      if (!response.ok || !data.success) {
        alert(data.error || 'Failed to update agent field');
        setIsSavingInline(false);
        return;
      }
      // Optimistically update the local agents list so the UI reflects the change immediately
      setAgents(prev => prev.map(a =>
        String(a._id) === String(agent._id)
          ? {
              ...a,
              agentArea: data.data?.agentArea ?? a.agentArea,
              agentStatus: data.data?.agentStatus ?? a.agentStatus,
              // Use an explicit key check so a null (cleared) date sticks
              // instead of falling back to the old value.
              pauseEndsAt: (data.data && 'pauseEndsAt' in data.data) ? data.data.pauseEndsAt : a.pauseEndsAt
            }
          : a
      ));
      closeInlineEdit();
      // Re-apply filters so the new value is respected if a filter is active
      setTimeout(() => applyFilters(), 0);
    } catch (err) {
      console.error('Inline edit failed:', err);
      alert('Error saving: ' + err.message);
      setIsSavingInline(false);
    }
  };

  const handleUpdateAgent = async (e) => {
    e.preventDefault();
    setIsUpdatingAgent(true);
    setEditError('');
    setEditSuccess('');

    try {
      const response = await fetch(`${API_URL}/admin/agents/${selectedAgentForDetails._id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${AuthService.getToken()}`
        },
        body: JSON.stringify({
          name: editAgentData.name,
          email: editAgentData.email,
          phone: editAgentData.phone,
          agency: editAgentData.agency,
          licenseNumber: editAgentData.licenseNumber,
          plan: editAgentData.plan,
          aliasEmail: editAgentData.aliasEmail || null,
          mlsCode: editAgentData.mlsCode || ''
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        // Entitlements live on PUT /users/:id/agent-fields, not the admin
        // agent-profile endpoint — same place agentArea and agentStatus are
        // validated. Deliberately a second call rather than duplicating the
        // enum check into /admin/agents: one server-side validator, one
        // source of truth.
        const entResponse = await fetch(
          `${API_URL}/users/${selectedAgentForDetails._id}/agent-fields`,
          {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${AuthService.getToken()}`
            },
            body: JSON.stringify({ entitlements: editAgentData.entitlements })
          }
        );
        const entData = await entResponse.json();
        if (!entResponse.ok || !entData.success) {
          // Profile saved, product access didn't. Say so precisely — a generic
          // success here would leave an admin believing they granted Studio
          // when they didn't.
          setEditError(
            'Profile saved, but product access did not update: ' +
            (entData.error || 'unknown error')
          );
          setIsUpdatingAgent(false);
          fetchAgents();
          return;
        }

        setEditSuccess('Agent profile updated successfully!');
        fetchAgents();
        
        // Update the selected agent details with new data
        setSelectedAgentForDetails(data.data);
        
        setTimeout(() => {
          closeEditAgentModal();
        }, 1500);
      } else {
        setEditError(data.error || 'Failed to update agent');
      }
    } catch (err) {
      setEditError('Error updating agent: ' + err.message);
    } finally {
      setIsUpdatingAgent(false);
    }
  };

  const openUserDetailsModal = (user) => {
    setSelectedUserForModal(user);
    setAdminUserModalOpen(true);
  };

  const closeUserDetailsModal = () => {
    setAdminUserModalOpen(false);
    setSelectedUserForModal(null);
  };

  // Referral Functions
  const openReferralModal = async (agent) => {
    setSelectedAgentForReferral(agent);
    setReferralModalOpen(true);
    setNewReferralData({
      campaignName: `${agent.name} - ${new Date().toLocaleDateString()}`,
      notes: ''
    });
    setReferralError('');
    setReferralSuccess('');

    try {
      const response = await fetch(`${API_URL}/referrals/agent/${agent._id}`, {
        headers: {
          'Authorization': `Bearer ${AuthService.getToken()}`
        }
      });
      const data = await response.json();
      setReferralLinks(data || []);
    } catch (err) {
      console.error('Error fetching agent referral links:', err);
      setReferralLinks([]);
    }
  };

  const closeReferralModal = () => {
    setReferralModalOpen(false);
    setSelectedAgentForReferral(null);
    setReferralLinks([]);
    setNewReferralData({ campaignName: '', notes: '' });
    setReferralError('');
    setReferralSuccess('');
  };

  const handleCreateReferralLink = async (e) => {
    e.preventDefault();
    setIsCreatingReferral(true);
    setReferralError('');
    setReferralSuccess('');

    try {
      const response = await fetch(`${API_URL}/referrals/admin/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${AuthService.getToken()}`
        },
        body: JSON.stringify({
          agentId: selectedAgentForReferral._id,
          campaignName: newReferralData.campaignName,
          notes: newReferralData.notes
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to create referral link');
      }

      setReferralSuccess('Referral link created successfully!');
      
      const refreshResponse = await fetch(`${API_URL}/referrals/agent/${selectedAgentForReferral._id}`, {
        headers: {
          'Authorization': `Bearer ${AuthService.getToken()}`
        }
      });
      const refreshData = await refreshResponse.json();
      setReferralLinks(refreshData || []);
      fetchAllReferralLinks();

      setNewReferralData({
        campaignName: `${selectedAgentForReferral.name} - ${new Date().toLocaleDateString()}`,
        notes: ''
      });

    } catch (error) {
      setReferralError(error.message || 'An error occurred while creating the referral link');
    } finally {
      setIsCreatingReferral(false);
    }
  };

  // Messaging Functions
  const openMessageModal = async (agent) => {
    setSelectedAgentForMessage(agent);
    setShowMessageModal(true);
    await startConversation(agent);
  };

  const closeMessageModal = () => {
    setShowMessageModal(false);
    setSelectedAgentForMessage(null);
    setSelectedConversation(null);
    setMessages([]);
    setMessageText('');
  };

  const startConversation = async (agent) => {
    try {
      const createResponse = await fetch(`${API_URL}/messages/conversations`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${AuthService.getToken()}`
        },
        body: JSON.stringify({
          userId: agent._id,
          subject: 'Message from Lauft Team'
        })
      });

      const createData = await createResponse.json();
      
      if (createData.success) {
        loadConversation(createData.data._id);
      } else if (createData.conversationId) {
        loadConversation(createData.conversationId);
      } else {
        setError(createData.error || 'Failed to create conversation');
      }
    } catch (err) {
      setError('Error starting conversation: ' + err.message);
    }
  };

  const loadConversation = async (conversationId) => {
    try {
      const response = await fetch(`${API_URL}/messages/conversations/${conversationId}`, {
        headers: {
          'Authorization': `Bearer ${AuthService.getToken()}`
        }
      });

      const data = await response.json();
      
      if (data.success) {
        setSelectedConversation(data.data.conversation);
        setMessages(data.data.messages || []);
        setTimeout(scrollToBottom, 100);
      }
    } catch (err) {
      console.error('Error loading conversation:', err);
    }
  };

  const sendMessage = async () => {
    if (!messageText.trim() || !selectedConversation) return;

    setIsSending(true);
    try {
      const response = await fetch(`${API_URL}/messages/conversations/${selectedConversation._id}/messages`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${AuthService.getToken()}`
        },
        body: JSON.stringify({
          content: messageText.trim()
        })
      });

      const data = await response.json();
      
      if (data.success) {
        setMessages([...messages, data.data]);
        setMessageText('');
        setTimeout(scrollToBottom, 100);
      } else {
        setError(data.error || 'Failed to send message');
      }
    } catch (err) {
      setError('Error sending message: ' + err.message);
    } finally {
      setIsSending(false);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      alert('Link copied to clipboard!');
    });
  };

  // Helper function to check if profile image is valid
  const hasValidProfileImage = (agent) => {
    return agent.profileImage && 
           agent.profileImage !== 'default-profile.jpg' && 
           agent.profileImage.startsWith('http');
  };

  return (
    <div className="admin-panel" style={{ 
      color: '#111',
      minHeight: '100vh',
      background: '#fafafa',
      padding: '32px'
    }}>
      <style>{`
        /* Agent HQ — Control Center design system */
        /* Palette: #fafafa bg, #fff cards, #2563eb primary, #111 text */

        .admin-panel .section-title {
          color: #111 !important;
          margin: 0 0 4px 0;
          font-size: 22px;
          font-weight: 700;
          letter-spacing: -0.01em;
        }

        .admin-panel .section-subtitle {
          color: #9ca3af;
          margin: 0 0 24px 0;
          font-size: 13px;
        }

        .admin-panel .alert-success {
          background-color: rgba(34, 197, 94, 0.08) !important;
          color: #166534 !important;
          border: 1px solid rgba(34, 197, 94, 0.25) !important;
          padding: 11px 14px;
          border-radius: 10px;
          margin-bottom: 16px;
          font-size: 13px;
          font-weight: 500;
        }

        .admin-panel .alert-danger {
          background-color: rgba(220, 38, 38, 0.08) !important;
          color: #991b1b !important;
          border: 1px solid rgba(220, 38, 38, 0.25) !important;
          padding: 11px 14px;
          border-radius: 10px;
          margin-bottom: 16px;
          font-size: 13px;
          font-weight: 500;
        }

        /* Search */
        .admin-panel .search-container {
          background: #fff;
          border-radius: 12px;
          padding: 12px 16px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
          border: 1px solid #e5e7eb;
          margin-bottom: 20px;
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .admin-panel .search-input {
          flex: 1;
          border: none;
          outline: none;
          font-size: 13px;
          color: #111;
          background: transparent;
        }

        .admin-panel .search-input::placeholder {
          color: #9ca3af;
        }

        /* Buttons */
        .admin-panel .btn {
          padding: 9px 18px;
          border-radius: 10px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.15s;
          font-size: 13px;
          letter-spacing: 0.01em;
          border: 1px solid #e5e7eb;
          display: inline-flex;
          align-items: center;
          gap: 8px;
          background: #fff;
          color: #6b7280;
        }

        .admin-panel .btn:hover {
          background: #f9fafb;
          border-color: #d1d5db;
          color: #111;
        }

        .admin-panel .btn-primary {
          background: linear-gradient(135deg, #2563eb, #1d4ed8);
          color: #fff;
          border: none;
          box-shadow: 0 2px 8px rgba(37, 99, 235, 0.35);
        }

        .admin-panel .btn-primary:hover {
          background: linear-gradient(135deg, #1d4ed8, #1e40af);
          color: #fff;
          box-shadow: 0 4px 12px rgba(37, 99, 235, 0.45);
          transform: translateY(-1px);
        }

        .admin-panel .btn-secondary {
          background: #fff;
          color: #6b7280;
          border: 1px solid #e5e7eb;
        }

        .admin-panel .btn-secondary:hover {
          background: #f9fafb;
          color: #111;
        }

        .admin-panel .btn-sm {
          padding: 6px 12px;
          font-size: 11px;
          border-radius: 8px;
        }

        .admin-panel .btn:disabled {
          opacity: 0.6;
          cursor: default;
          transform: none;
        }

        /* Agent grid */
        .admin-panel .agents-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
          gap: 20px;
        }

        /* Tabs — pill group matching Control Center */
        .admin-panel .tabs-container {
          display: inline-flex;
          background: rgba(26, 26, 26, 0.06);
          border-radius: 999px;
          padding: 3px;
          gap: 2px;
          margin-bottom: 24px;
          width: fit-content;
          border: none;
        }

        .admin-panel .tab-button {
          padding: 6px 16px;
          background: transparent;
          border: none;
          color: #9ca3af;
          font-weight: 600;
          font-size: 11px;
          letter-spacing: 0.04em;
          cursor: pointer;
          border-radius: 999px;
          transition: all 0.15s;
          margin-bottom: 0;
        }

        .admin-panel .tab-button:hover {
          color: #111;
          background: transparent;
        }

        .admin-panel .tab-button.active {
          color: #111;
          background: #fff;
          box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
        }

        /* Pagination */
        .admin-panel .pagination-container {
          display: flex;
          justify-content: center;
          align-items: center;
          gap: 8px;
          margin-top: 28px;
        }

        .admin-panel .pagination-btn {
          padding: 7px 14px;
          background: #fff;
          border: 1px solid #e5e7eb;
          border-radius: 9px;
          color: #6b7280;
          font-weight: 600;
          font-size: 12px;
          cursor: pointer;
          transition: all 0.15s;
        }

        .admin-panel .pagination-btn:hover:not(:disabled) {
          background: #f9fafb;
          border-color: #2563eb;
          color: #2563eb;
        }

        .admin-panel .pagination-btn:disabled {
          opacity: 0.4;
          cursor: not-allowed;
        }

        .admin-panel .page-info {
          padding: 7px 12px;
          color: #9ca3af;
          font-size: 12px;
          font-weight: 500;
        }

        /* Modals */
        .admin-panel-modal-overlay,
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(17, 17, 17, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          backdrop-filter: blur(6px);
          -webkit-backdrop-filter: blur(6px);
        }

        .modal-content {
          background: #fff;
          border-radius: 16px;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
          border: none;
          width: 520px;
          max-width: 92vw;
          max-height: 90vh;
          overflow: hidden;
          display: flex;
          flex-direction: column;
        }

        .modal-content-lg {
          width: 720px;
        }

        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 20px 24px;
          border-bottom: 1px solid #f0f0f0;
          background: #fff;
        }

        .modal-header h3 {
          margin: 0;
          font-size: 16px;
          font-weight: 700;
          color: #111;
          letter-spacing: -0.01em;
        }

        .modal-close {
          background: none;
          border: none;
          cursor: pointer;
          padding: 6px;
          border-radius: 8px;
          color: #9ca3af;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.15s;
        }

        .modal-close:hover {
          background: #f3f4f6;
          color: #111;
        }

        .modal-body {
          padding: 24px;
          overflow-y: auto;
          flex: 1;
          background: #fff;
        }

        .modal-footer {
          padding: 16px 24px;
          border-top: 1px solid #f0f0f0;
          display: flex;
          justify-content: flex-end;
          gap: 10px;
          background: #fff;
        }

        /* Forms */
        .admin-panel .form-group {
          margin-bottom: 16px;
        }

        .admin-panel .form-label,
        .modal-body .form-label {
          display: block;
          color: #9ca3af;
          margin-bottom: 6px;
          font-weight: 600;
          font-size: 11px;
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }

        .admin-panel .form-control,
        .modal-body .form-control {
          width: 100%;
          padding: 9px 12px;
          border: 1px solid #e5e7eb;
          border-radius: 9px;
          font-size: 13px;
          color: #111;
          background: #fff;
          transition: all 0.15s;
          box-sizing: border-box;
          font-family: inherit;
        }

        .admin-panel .form-control:focus,
        .modal-body .form-control:focus {
          outline: none;
          border-color: #2563eb;
          box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.12);
        }

        .admin-panel .form-control::placeholder,
        .modal-body .form-control::placeholder {
          color: #9ca3af;
        }

        /* Stats tiles */
        .stats-card {
          background: #fff;
          border-radius: 12px;
          padding: 14px 16px;
          text-align: left;
          border: 1px solid #f0f0f0;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.03);
        }

        .stats-value {
          font-size: 22px;
          font-weight: 700;
          color: #111;
          line-height: 1.1;
        }

        .stats-label {
          font-size: 11px;
          font-weight: 600;
          color: #9ca3af;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          margin-top: 4px;
        }

        /* List items */
        .user-list-item {
          display: flex;
          align-items: center;
          padding: 10px 12px;
          border-radius: 10px;
          cursor: pointer;
          transition: all 0.15s;
        }

        .user-list-item:hover {
          background: #f9fafb;
        }

        /* Empty / loading */
        .admin-panel .empty-state,
        .modal-body .empty-state {
          text-align: center;
          padding: 40px 20px;
          color: #9ca3af;
          font-size: 13px;
        }

        .admin-panel .loading-spinner,
        .modal-body .loading-spinner {
          text-align: center;
          padding: 40px 20px;
          color: #9ca3af;
          font-size: 13px;
        }

        /* Inline code */
        .admin-panel code,
        .modal-body code {
          background: rgba(37, 99, 235, 0.08);
          color: #1d4ed8;
          padding: 3px 8px;
          border-radius: 6px;
          font-family: 'SF Mono', Menlo, Monaco, Consolas, monospace;
          font-size: 12px;
          font-weight: 600;
        }

        /* Form section cards */
        .form-section {
          background: #fff;
          border-radius: 12px;
          padding: 24px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
          border: 1px solid #f0f0f0;
          margin-bottom: 20px;
        }

        .form-section-title {
          font-size: 13px;
          font-weight: 700;
          color: #111;
          margin: 0 0 20px 0;
          padding-bottom: 12px;
          border-bottom: 1px solid #f0f0f0;
          letter-spacing: -0.005em;
        }

        .form-row {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 14px;
        }

        @media (max-width: 640px) {
          .form-row {
            grid-template-columns: 1fr;
          }
          .admin-panel .agents-grid {
            grid-template-columns: 1fr;
          }
        }

        /* Referrals / data tables */
        .admin-panel .data-table {
          width: 100%;
          border-collapse: collapse;
          background: #fff;
          border-radius: 12px;
          overflow: hidden;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
          border: 1px solid #f0f0f0;
        }

        .admin-panel .data-table th {
          background: #fafafa;
          padding: 11px 14px;
          text-align: left;
          font-weight: 700;
          font-size: 10px;
          color: #9ca3af;
          text-transform: uppercase;
          letter-spacing: 0.08em;
          border-bottom: 1px solid #f0f0f0;
        }

        .admin-panel .data-table td {
          padding: 12px 14px;
          border-bottom: 1px solid #f5f5f5;
          color: #111;
          font-size: 13px;
        }

        .admin-panel .data-table tr:last-child td {
          border-bottom: none;
        }

        .admin-panel .data-table tr:hover {
          background: #fafafa;
        }

        /* Dashboard card — white panel container matching Control Center */
        .admin-panel .dashboard-card {
          background: #fff;
          border-radius: 14px;
          padding: 20px;
          border: 1px solid #f0f0f0;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
        }

        .admin-panel .card-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 16px;
        }

        .admin-panel .card-header h3 {
          margin: 0;
          font-size: 11px;
          font-weight: 600;
          color: #9ca3af;
          letter-spacing: 0.06em;
          text-transform: uppercase;
        }

        /* Table container — removes outer border when nested in dashboard-card */
        .admin-panel .dashboard-card .table-container {
          margin: 0 -20px -20px;
          overflow-x: auto;
          overflow-y: auto;
          max-height: 560px;
        }

        /* Custom scrollbar — subtle, Control Center style */
        .admin-panel .dashboard-card .table-container::-webkit-scrollbar {
          width: 8px;
          height: 8px;
        }
        .admin-panel .dashboard-card .table-container::-webkit-scrollbar-track {
          background: transparent;
        }
        .admin-panel .dashboard-card .table-container::-webkit-scrollbar-thumb {
          background: #e5e7eb;
          border-radius: 4px;
        }
        .admin-panel .dashboard-card .table-container::-webkit-scrollbar-thumb:hover {
          background: #d1d5db;
        }

        .admin-panel .dashboard-card .data-table {
          border-radius: 0;
          border: none;
          box-shadow: none;
          border-top: 1px solid #f0f0f0;
        }

        /* Sticky table header so column labels stay visible while scrolling rows */
        .admin-panel .dashboard-card .data-table thead th {
          position: sticky;
          top: 0;
          z-index: 2;
          background: #fafafa;
        }

        /* Employee cell — avatar + stacked info */
        .admin-panel .employee-cell {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .admin-panel .employee-avatar {
          width: 36px;
          height: 36px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .admin-panel .employee-info {
          display: flex;
          flex-direction: column;
          gap: 1px;
        }

        .admin-panel .employee-name {
          font-size: 13px;
          font-weight: 600;
          display: inline-flex;
          align-items: center;
          max-width: 240px;
        }

        /* Loading / empty states inside dashboard-card */
        .admin-panel .loading-state {
          text-align: center;
          padding: 48px 20px;
          color: #9ca3af;
          font-size: 13px;
        }

        .admin-panel .loading-state .spinner {
          width: 28px;
          height: 28px;
          border: 3px solid #f0f0f0;
          border-top-color: #2563eb;
          border-radius: 50%;
          margin: 0 auto 12px;
          animation: admin-spin 0.8s linear infinite;
        }

        @keyframes admin-spin {
          to { transform: rotate(360deg); }
        }

        .admin-panel .btn-create {
          padding: 9px 18px;
          border-radius: 10px;
          border: none;
          background: linear-gradient(135deg, #2563eb, #1d4ed8);
          color: #fff;
          font-size: 13px;
          font-weight: 600;
          letter-spacing: 0.01em;
          cursor: pointer;
          box-shadow: 0 2px 8px rgba(37, 99, 235, 0.35);
          margin-top: 16px;
        }

        .admin-panel .btn-create:hover {
          background: linear-gradient(135deg, #1d4ed8, #1e40af);
          box-shadow: 0 4px 12px rgba(37, 99, 235, 0.45);
          transform: translateY(-1px);
        }
      `}</style>
      
      {/* Agent HQ branding */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
        <img src="/lauft-logo.png" alt="Lauft" style={{ width: '22px', height: '22px', objectFit: 'contain' }} />
        <span style={{ fontSize: '11px', fontWeight: '600', letterSpacing: '0.08em', color: '#888', textTransform: 'uppercase' }}>Agent HQ</span>
      </div>



      {error && <div className="alert-danger">{error}</div>}
      {success && <div className="alert-success">{success}</div>}

      {/* Create Agent Form */}
      {showCreateForm && (
        <form onSubmit={handleSubmit} style={{ marginBottom: '32px' }}>
          <div className="form-section">
            <h4 className="form-section-title">Account Information</h4>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Full Name *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className="form-control"
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">Email Address *</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="form-control"
                  required
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Password *</label>
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  className="form-control"
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">Phone Number</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="form-control"
                />
              </div>
            </div>
          </div>

          <div className="form-section">
            <h4 className="form-section-title">Agent Details</h4>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Brokerage / Agency</label>
                <input
                  type="text"
                  name="agency"
                  value={formData.agentInfo.agency}
                  onChange={handleChange}
                  className="form-control"
                />
              </div>
              <div className="form-group">
                <label className="form-label">License Number</label>
                <input
                  type="text"
                  name="licenseNumber"
                  value={formData.agentInfo.licenseNumber}
                  onChange={handleChange}
                  className="form-control"
                />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Subscription Plan</label>
              <select
                name="plan"
                value={formData.agentInfo.plan}
                onChange={handleChange}
                className="form-control"
              >
                <option value="Launch Advantage">Launch Advantage</option>
                <option value="Launch Premium">Launch Premium</option>
                <option value="Ground Floor">Ground Floor</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Product Access</label>
              {/* Entitlements control which portals this account can reach.
                  Separate from `plan` above — that's the Pro PRICING TIER, this
                  is which products exist for them at all. An agent can hold
                  several. */}
              <div style={{ display: 'flex', gap: '18px', flexWrap: 'wrap', padding: '9px 0' }}>
                {[
                  { key: 'pro', label: 'Lauft Pro', hint: 'partner portal, leads, points' },
                  { key: 'studio', label: 'Studio', hint: 'AI listing videos' },
                  { key: 'lease', label: 'Lease', hint: 'not yet live' }
                ].map(({ key, label, hint }) => (
                  <label
                    key={key}
                    style={{ display: 'flex', alignItems: 'flex-start', gap: '7px', cursor: 'pointer', fontSize: '13px' }}
                  >
                    <input
                      type="checkbox"
                      checked={(formData.entitlements || []).includes(key)}
                      onChange={() => toggleEntitlement(key)}
                      style={{ marginTop: '2px', cursor: 'pointer' }}
                    />
                    <span>
                      <span style={{ fontWeight: 600, color: '#111' }}>{label}</span>
                      <span style={{ display: 'block', fontSize: '11px', color: '#9ca3af' }}>{hint}</span>
                    </span>
                  </label>
                ))}
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Display Email (Alias)</label>
              <input
                type="email"
                name="aliasEmail"
                value={formData.agentInfo.aliasEmail}
                onChange={handleChange}
                className="form-control"
                placeholder="Optional - shown to users instead of login email"
              />
              <small style={{ color: '#9ca3af', fontSize: '0.75rem', marginTop: '4px', display: 'block' }}>
                If set, this email will be displayed to users. The login email will still be used for authentication and system notifications.
              </small>
            </div>
          </div>

          <div style={{ display: 'flex', gap: '12px' }}>
            <button type="button" onClick={() => setShowCreateForm(false)} className="btn btn-secondary">
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={isLoading}>
              {isLoading ? 'Creating...' : 'Create Agent Account'}
            </button>
          </div>
        </form>
      )}

      {/* Status Tabs — filter roster by agent status */}
      <style>{`
        @keyframes statusPulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.35; }
        }
      `}</style>
      <div className="tabs-container">
        {[
          { key: 'active',   label: 'Active',   dot: '#10b981', animated: true  },
          { key: 'past_due', label: 'Past Due', dot: '#ea580c', animated: true  },
          { key: 'paused',   label: 'Paused',   dot: '#eab308', animated: false },
          { key: 'inactive', label: 'Inactive', dot: '#9ca3af', animated: false },
          { key: 'beta',     label: 'Beta',     dot: '#8b5cf6', animated: false },
        ].map(t => {
          const isActive = activeTab === t.key;
          return (
            <button
              key={t.key}
              className={`tab-button ${isActive ? 'active' : ''}`}
              onClick={() => setActiveTab(t.key)}
              style={{ display: 'inline-flex', alignItems: 'center', gap: '7px', textTransform: 'none', letterSpacing: '0.01em', fontWeight: isActive ? 600 : 500 }}
            >
              {isActive && (
                <span style={{
                  width: '7px',
                  height: '7px',
                  borderRadius: '50%',
                  background: t.dot,
                  display: 'inline-block',
                  flexShrink: 0,
                  animation: t.animated ? 'statusPulse 1.8s ease-in-out infinite' : 'none',
                  boxShadow: t.animated ? `0 0 0 3px ${t.dot}22` : 'none',
                }} />
              )}
              {t.label}
            </button>
          );
        })}
      </div>

      {/* Agent Roster (always rendered, filtered by status) */}
      <>
          {/* Search */}
          <div className="search-container">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2">
              <circle cx="11" cy="11" r="8"/>
              <path d="m21 21-4.35-4.35"/>
            </svg>
            <input
              type="text"
              placeholder="Search agents by name, email, or agency..."
              value={searchTerm}
              onChange={(e) => handleSearch(e.target.value)}
              className="search-input"
            />
          </div>

          {/* Agent Table */}
          <div className="dashboard-card">
            <div className="card-header">
              <h3>
                {(() => {
                  const statusLabels = { active: 'Active', past_due: 'Past Due', paused: 'Paused', inactive: 'Inactive', beta: 'Beta' };
                  const base = statusLabels[activeTab] || 'All';
                  const suffix = territoryFilter === 'all'
                    ? ' Agents'
                    : territoryFilter === 'unassigned'
                      ? ' · Unassigned'
                      : ` · ${territoryFilter}`;
                  return `${base}${suffix}`;
                })()}
                <span style={{ marginLeft: '8px', fontSize: '10px', fontWeight: '600', color: '#d1d5db', letterSpacing: '0.05em' }}>
                  {filteredAgents.length}
                </span>
              </h3>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <select
                  value={territoryFilter}
                  onChange={e => handleTerritoryFilterChange(e.target.value)}
                  style={{
                    padding: '5px 28px 5px 10px',
                    borderRadius: '7px',
                    border: '1px solid #e5e7eb',
                    background: '#fff',
                    fontSize: '11px',
                    fontWeight: '600',
                    color: territoryFilter === 'all' ? '#6b7280' : '#2563eb',
                    cursor: 'pointer',
                    outline: 'none',
                    appearance: 'none',
                    WebkitAppearance: 'none',
                    MozAppearance: 'none',
                    backgroundImage: `url("data:image/svg+xml;charset=UTF-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='10' viewBox='0 0 24 24' fill='none' stroke='%239ca3af' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`,
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'right 8px center',
                    letterSpacing: '0.02em',
                  }}
                  title="Filter by territory"
                  disabled={territoriesLoading || !!territoriesError}
                >
                  <option value="all">
                    {territoriesLoading ? 'Loading markets…' : territoriesError ? 'Markets unavailable' : 'All Territories'}
                  </option>
                  {territoryNames.map(t => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                  <option value="unassigned">Unassigned</option>
                </select>
<button
                  onClick={() => setTrackedLinksOpen(true)}
                  title="Tracked links"
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '28px', height: '28px', borderRadius: '7px', background: '#fff', border: '1.5px solid #e5e7eb', cursor: 'pointer', color: '#6b7280', flexShrink: 0 }}
                >
                  <Link size={14} />
                </button>
                <button
                  onClick={exportAgentsCSV}
                  title="Export agents CSV"
                  disabled={agents.length === 0}
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '28px', height: '28px', borderRadius: '7px', background: '#fff', border: '1.5px solid #e5e7eb', cursor: agents.length === 0 ? 'not-allowed' : 'pointer', color: '#6b7280', flexShrink: 0, opacity: agents.length === 0 ? 0.5 : 1 }}
                >
                  <Upload size={14} />
                </button>
                <button
                  onClick={() => setShowCreateForm(!showCreateForm)}
                  title={showCreateForm ? 'Cancel' : 'Add Agent'}
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '28px', height: '28px', borderRadius: '7px', background: '#fff', border: '1.5px solid #2563eb', cursor: 'pointer', color: '#2563eb', flexShrink: 0 }}
                >
                  <span style={{ fontSize: '18px', lineHeight: 1, fontWeight: '300', marginTop: '-1px', transform: showCreateForm ? 'rotate(45deg)' : 'none', transition: 'transform 0.15s' }}>+</span>
                </button>
              </div>
            </div>

            {isFetchingAgents ? (
              <div className="loading-state">
                <div className="spinner"></div>
                <p>Loading agents...</p>
              </div>
            ) : filteredAgents.length === 0 ? (
              <div className="empty-state">
                <Users size={40} color="#e5e7eb" style={{ marginBottom: '12px' }} />
                <p>No agents found</p>
              </div>
            ) : (
              <div className="table-container">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th style={{ minWidth: '240px' }}>Agent</th>
                      <th style={{ textAlign: 'left' }}>Territory</th>
                      <th style={{ textAlign: 'left' }}>Status</th>
                      <th style={{ textAlign: 'left' }}>Points</th>
                      <th style={{ textAlign: 'left' }}>Users</th>
                      <th style={{ textAlign: 'left' }}>Invites</th>
                      <th style={{ textAlign: 'left' }}>Matches</th>
                      <th style={{ textAlign: 'left' }}>Messages</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(() => {
                      // Newest-first: createdAt descending, ObjectId fallback (ObjectIds are time-ordered)
                      const sorted = [...getCurrentPageAgents()].sort((a, b) => {
                        const ta = a.createdAt ? new Date(a.createdAt).getTime() : 0;
                        const tb = b.createdAt ? new Date(b.createdAt).getTime() : 0;
                        if (ta !== tb) return tb - ta;
                        return String(b._id).localeCompare(String(a._id));
                      });
                      // Muted numeric cell renderer — returns "—" when nullish
                      const NumCell = ({ value }) => {
                        if (value === null || value === undefined) {
                          return <span style={{ fontSize: '11px', color: '#d1d5db' }}>—</span>;
                        }
                        return <strong style={{ color: value > 0 ? '#111' : '#d1d5db' }}>{value}</strong>;
                      };
                      return sorted.map(agent => {
                        const isTeamLead = getTeamMemberCount(agent._id) > 0;
                        const isTeamMember = !!agent.teamLead;
                        // Optional fields (backend hasn't been wired yet — render gracefully)
                        const invites = agent.invitationCount ?? agent.invitationsCount ?? null;
                        const matches = agent.matchesSentCount ?? agent.matchesCount ?? null;
                        const messages = agent.messagesSentCount ?? agent.messagesCount ?? null;
                        return (
                          <tr key={agent._id}>
                            <td style={{ minWidth: '240px', textAlign: 'left' }}>
                              <div className="employee-cell" onClick={() => openAgentDetailsModal(agent)} style={{ cursor: 'pointer' }} title="Click to view details">
                                <div className="employee-avatar" style={{ overflow: 'hidden', background: hasValidProfileImage(agent) ? 'transparent' : '#f3f4f6', flexShrink: 0 }}>
                                  {hasValidProfileImage(agent)
                                    ? <img src={agent.profileImage} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} />
                                    : <svg viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ width: '100%', height: '100%' }}><rect width="40" height="40" fill="#e5e7eb" rx="20"/><circle cx="20" cy="15" r="7" fill="#9ca3af"/><path d="M4 36c0-8.837 7.163-16 16-16s16 7.163 16 16" fill="#9ca3af"/></svg>
                                  }
                                </div>
                                <div className="employee-info" style={{ minWidth: 0, textAlign: 'left' }}>
                                  <span className="employee-name" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', color: '#2563eb', textAlign: 'left' }}>
                                    {agent.name}
                                    {/* Product badges. Pro is DELIBERATELY unbadged — it's the
                                        default and the overwhelming majority, so badging it
                                        would put a label on nearly every row and make the
                                        genuinely different accounts harder to spot, not
                                        easier. Only the exceptions get a mark.

                                        Rendered per entitlement rather than as one badge, so
                                        an account holding both Studio and Lease shows both. */}
                                    {(agent.entitlements || []).includes('lease') && (
                                      <span style={{ marginLeft: '6px', fontSize: '10px', fontWeight: '700', color: '#0e7490', background: 'rgba(6,182,212,0.12)', padding: '1px 6px', borderRadius: '999px' }} title="Leasing agent — intro credits, no lead claiming">Lease</span>
                                    )}
                                    {(agent.entitlements || []).includes('studio') && (
                                      <span style={{ marginLeft: '6px', fontSize: '10px', fontWeight: '700', color: '#7c3aed', background: 'rgba(139,92,246,0.12)', padding: '1px 6px', borderRadius: '999px' }} title="Lauft Studio — AI listing videos">Studio</span>
                                    )}
                                    {isTeamLead && (
                                      <span style={{ marginLeft: '6px', fontSize: '10px', fontWeight: '700', color: '#4f46e5', background: 'rgba(99,102,241,0.1)', padding: '1px 6px', borderRadius: '999px' }}>Team Lead</span>
                                    )}
                                    {isTeamMember && !isTeamLead && (
                                      <span style={{ marginLeft: '6px', fontSize: '10px', fontWeight: '700', color: '#16a34a', background: 'rgba(34,197,94,0.1)', padding: '1px 6px', borderRadius: '999px' }}>Team</span>
                                    )}
                                    {isAgentLocked(agent) && (
                                      <button
                                        type="button"
                                        onClick={(e) => { e.stopPropagation(); toggleAgentLock(agent, false); }}
                                        disabled={lockBusyId === agent._id}
                                        title="Locked until their onboarding call. Click to give full portal access."
                                        style={{
                                          marginLeft: '6px',
                                          fontSize: '10px',
                                          fontWeight: '700',
                                          color: '#ea580c',
                                          background: 'rgba(234,88,12,0.1)',
                                          border: '1px solid rgba(234,88,12,0.25)',
                                          padding: '1px 6px',
                                          borderRadius: '999px',
                                          cursor: lockBusyId === agent._id ? 'wait' : 'pointer',
                                          fontFamily: 'inherit',
                                          verticalAlign: 'middle',
                                        }}
                                      >
                                        {lockBusyId === agent._id ? '...' : 'Locked'}
                                      </button>
                                    )}
                                  </span>
                                  {agent.phone && (
                                    <span style={{ display: 'block', fontSize: '11px', color: '#6b7280', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '240px', textAlign: 'left' }}>
                                      {agent.phone}
                                    </span>
                                  )}
                                  <span style={{ display: 'block', fontSize: '11px', color: '#6b7280', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '240px', textAlign: 'left' }}>
                                    {agent.agentInfo?.aliasEmail || agent.email}
                                  </span>
                                  {agent.agentInfo?.agency && (
                                    <span style={{ display: 'block', fontSize: '11px', color: '#9ca3af', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '240px', textAlign: 'left' }}>
                                      {agent.agentInfo.agency}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </td>
                            <td style={{ textAlign: 'left' }}>
                              {inlineEditAgentId === agent._id && inlineEditField === 'area' ? (
                                <div style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                  <select
                                    value={inlineEditValue}
                                    onChange={(e) => setInlineEditValue(e.target.value)}
                                    autoFocus
                                    style={{ fontSize: '11px', padding: '3px 6px', borderRadius: '6px', border: '1px solid #e5e7eb', fontFamily: 'inherit' }}
                                  >
                                    <option value="">Unassigned</option>
                                    {/* A legacy agentArea that's no longer an active
                                        market still needs to render, or the select
                                        would silently blank it out on open. */}
                                    {inlineEditValue && !territoryNames.includes(inlineEditValue) && (
                                      <option value={inlineEditValue}>{inlineEditValue}</option>
                                    )}
                                    {territoryNames.map(t => (
                                      <option key={t} value={t}>{t}</option>
                                    ))}
                                  </select>
                                  <button
                                    type="button"
                                    onClick={() => handleInlineEditSave(agent)}
                                    disabled={isSavingInline}
                                    style={{ fontSize: '10px', padding: '3px 8px', borderRadius: '6px', border: 'none', background: '#2563eb', color: '#fff', cursor: 'pointer', fontWeight: 600, fontFamily: 'inherit' }}
                                  >
                                    {isSavingInline ? '...' : 'Save'}
                                  </button>
                                  <button
                                    type="button"
                                    onClick={closeInlineEdit}
                                    style={{ fontSize: '10px', padding: '3px 8px', borderRadius: '6px', border: '1px solid #e5e7eb', background: '#fff', color: '#6b7280', cursor: 'pointer', fontFamily: 'inherit' }}
                                  >
                                    Cancel
                                  </button>
                                </div>
                              ) : agent.agentArea ? (
                                <button
                                  type="button"
                                  onClick={() => openInlineEdit(agent, 'area')}
                                  title="Click to change territory"
                                  style={{
                                    display: 'inline-block',
                                    padding: '2px 7px',
                                    borderRadius: '999px',
                                    fontSize: '9px',
                                    fontWeight: '600',
                                    color: '#6b7280',
                                    background: '#f3f4f6',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.04em',
                                    whiteSpace: 'nowrap',
                                    border: 'none',
                                    cursor: 'pointer',
                                    fontFamily: 'inherit',
                                  }}
                                >
                                  {agent.agentArea}
                                </button>
                              ) : (
                                <button
                                  type="button"
                                  onClick={() => openInlineEdit(agent, 'area')}
                                  title="Click to assign territory"
                                  style={{
                                    display: 'inline-block',
                                    padding: '2px 7px',
                                    borderRadius: '999px',
                                    fontSize: '9px',
                                    fontWeight: '600',
                                    color: '#9ca3af',
                                    background: 'transparent',
                                    border: '1px dashed #e5e7eb',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.04em',
                                    whiteSpace: 'nowrap',
                                    cursor: 'pointer',
                                    fontFamily: 'inherit',
                                  }}
                                >
                                  — Assign
                                </button>
                              )}
                            </td>
                            <td style={{ textAlign: 'left' }}>
                              {(() => {
                                const STATUS_COLORS = {
                                  active:   { color: '#16a34a', bg: 'rgba(22, 163, 74, 0.1)', label: 'Active' },
                                  past_due: { color: '#ea580c', bg: 'rgba(234, 88, 12, 0.1)', label: 'Past Due' },
                                  paused:   { color: '#eab308', bg: 'rgba(234, 179, 8, 0.12)', label: 'Paused' },
                                  inactive: { color: '#dc2626', bg: 'rgba(220, 38, 38, 0.1)', label: 'Inactive' },
                                  beta:     { color: '#7c3aed', bg: 'rgba(139, 92, 246, 0.12)', label: 'Beta' },
                                };
                                const statusKey = agent.agentStatus || 'active';
                                const sConfig = STATUS_COLORS[statusKey] || STATUS_COLORS.active;
                                if (inlineEditAgentId === agent._id && inlineEditField === 'status') {
                                  return (
                                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                      <select
                                        value={inlineEditValue}
                                        onChange={(e) => setInlineEditValue(e.target.value)}
                                        autoFocus
                                        style={{ fontSize: '11px', padding: '3px 6px', borderRadius: '6px', border: '1px solid #e5e7eb', fontFamily: 'inherit' }}
                                      >
                                        <option value="active">Active</option>
                                        <option value="past_due">Past Due</option>
                                        <option value="paused">Paused</option>
                                        <option value="inactive">Inactive</option>
                                        <option value="beta">Beta</option>
                                      </select>
                                      <button
                                        type="button"
                                        onClick={() => handleInlineEditSave(agent)}
                                        disabled={isSavingInline}
                                        style={{ fontSize: '10px', padding: '3px 8px', borderRadius: '6px', border: 'none', background: '#2563eb', color: '#fff', cursor: 'pointer', fontWeight: 600, fontFamily: 'inherit' }}
                                      >
                                        {isSavingInline ? '...' : 'Save'}
                                      </button>
                                      <button
                                        type="button"
                                        onClick={closeInlineEdit}
                                        style={{ fontSize: '10px', padding: '3px 8px', borderRadius: '6px', border: '1px solid #e5e7eb', background: '#fff', color: '#6b7280', cursor: 'pointer', fontFamily: 'inherit' }}
                                      >
                                        Cancel
                                      </button>
                                    </div>
                                  );
                                }
                                // Inline editor for the pause end date (only
                                // reachable when the agent is paused — the
                                // trigger below is gated on statusKey).
                                if (inlineEditAgentId === agent._id && inlineEditField === 'pauseEnd') {
                                  return (
                                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                      <input
                                        type="date"
                                        value={inlineEditValue}
                                        min={toDateInputValue(new Date())}
                                        onChange={(e) => setInlineEditValue(e.target.value)}
                                        autoFocus
                                        style={{ fontSize: '11px', padding: '3px 6px', borderRadius: '6px', border: '1px solid #e5e7eb', fontFamily: 'inherit' }}
                                      />
                                      <button
                                        type="button"
                                        onClick={() => handleInlineEditSave(agent)}
                                        disabled={isSavingInline || !inlineEditValue}
                                        style={{ fontSize: '10px', padding: '3px 8px', borderRadius: '6px', border: 'none', background: '#2563eb', color: '#fff', cursor: (isSavingInline || !inlineEditValue) ? 'default' : 'pointer', opacity: (isSavingInline || !inlineEditValue) ? 0.6 : 1, fontWeight: 600, fontFamily: 'inherit' }}
                                      >
                                        {isSavingInline ? '...' : 'Save'}
                                      </button>
                                      <button
                                        type="button"
                                        onClick={closeInlineEdit}
                                        style={{ fontSize: '10px', padding: '3px 8px', borderRadius: '6px', border: '1px solid #e5e7eb', background: '#fff', color: '#6b7280', cursor: 'pointer', fontFamily: 'inherit' }}
                                      >
                                        Cancel
                                      </button>
                                    </div>
                                  );
                                }
                                return (
                                  <div style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-start', gap: '3px' }}>
                                  <button
                                    type="button"
                                    onClick={() => openInlineEdit(agent, 'status')}
                                    title="Click to change status"
                                    style={{
                                      display: 'inline-block',
                                      padding: '2px 7px',
                                      borderRadius: '999px',
                                      fontSize: '9px',
                                      fontWeight: '600',
                                      color: sConfig.color,
                                      background: sConfig.bg,
                                      textTransform: 'uppercase',
                                      letterSpacing: '0.04em',
                                      whiteSpace: 'nowrap',
                                      border: 'none',
                                      cursor: 'pointer',
                                      fontFamily: 'inherit',
                                    }}
                                  >
                                    {sConfig.label}
                                  </button>
                                  {statusKey === 'paused' && (
                                    <div
                                      onClick={() => openInlineEdit(agent, 'pauseEnd')}
                                      title="Click to set/adjust pause end date"
                                      style={{ fontSize: '9px', color: '#a16207', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '3px', whiteSpace: 'nowrap' }}
                                    >
                                      <span style={{ opacity: 0.75 }}>Ends:</span>
                                      <span style={{ fontWeight: 700, textDecoration: 'underline', textUnderlineOffset: '2px' }}>
                                        {agent.pauseEndsAt ? new Date(agent.pauseEndsAt).toLocaleDateString() : 'Set date'}
                                      </span>
                                    </div>
                                  )}
                                  </div>
                                );
                              })()}
                            </td>
                            <td style={{ textAlign: 'left' }}>
                              <strong style={{ color: (agent.pointsBalance || 0) > 0 ? '#2563eb' : '#d1d5db' }}>
                                {agent.pointsBalance || 0}
                              </strong>
                            </td>
                            <td style={{ textAlign: 'left' }}><NumCell value={agent.assignedUsersCount || 0} /></td>
                            <td style={{ textAlign: 'left' }}><NumCell value={invites} /></td>
                            <td style={{ textAlign: 'left' }}><NumCell value={matches} /></td>
                            <td style={{ textAlign: 'left' }}><NumCell value={messages} /></td>
                          </tr>
                        );
                      });
                    })()}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Pagination */}
          {!isFetchingAgents && filteredAgents.length > 0 && totalPages > 1 && (
            <div className="pagination-container">
              <button
                className="pagination-btn"
                onClick={() => handlePageChange(1)}
                disabled={currentPage === 1}
                title="First page"
              >
                «
              </button>
              <button
                className="pagination-btn"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              >
                Previous
              </button>
              <span className="page-info">
                Page {currentPage} of {totalPages}
              </span>
              <button
                className="pagination-btn"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                Next
              </button>
              <button
                className="pagination-btn"
                onClick={() => handlePageChange(totalPages)}
                disabled={currentPage === totalPages}
                title="Last page"
              >
                »
              </button>
            </div>
          )}
        </>



      {/* Agent Details Modal */}
      {showAgentDetailsModal && selectedAgentForDetails && (
        <AgentDetailsModal
          agent={selectedAgentForDetails}
          onClose={closeAgentDetailsModal}
          onAgentDeleted={fetchAgents}
          onAgentUpdated={(updated) => { if (updated?._id) setSelectedAgentForDetails(updated); else fetchAgents(); }}
          onEdit={(a) => { openEditAgentModal(a); }}
          onAddPoints={(a) => { closeAgentDetailsModal(); openAddPointsModal(a); }}
          onMessage={(a) => { closeAgentDetailsModal(); openMessageModal(a); }}
          onReferrals={(a) => { closeAgentDetailsModal(); openReferralModal(a); }}
          onResetPassword={(a) => { closeAgentDetailsModal(); openResetModal(a); }}
          onViewUser={openUserDetailsModal}
          onRefundUser={(u) => handleRefundUser(u, selectedAgentForDetails)}
        />
      )}

      {/* Password Reset Modal */}
      {resetModalOpen && selectedAgent && (
        <div className="modal-overlay" onClick={closeResetModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Reset Password</h3>
              <button className="modal-close" onClick={closeResetModal}>
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handlePasswordReset}>
              <div className="modal-body">
                <p style={{ color: '#6b7280', marginBottom: '20px' }}>
                  Reset password for <strong>{selectedAgent.name}</strong>
                </p>
                {resetError && <div className="alert-danger">{resetError}</div>}
                {resetSuccess && <div className="alert-success">{resetSuccess}</div>}
                <div className="form-group">
                  <label className="form-label">New Password</label>
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="form-control"
                    placeholder="Enter new password (min 5 characters)"
                    required
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={closeResetModal}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={isResetting}>
                  {isResetting ? 'Resetting...' : 'Reset Password'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Points Modal */}
      {addPointsModalOpen && selectedAgentForPoints && (
        <div className="modal-overlay" onClick={closeAddPointsModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Add Points</h3>
              <button className="modal-close" onClick={closeAddPointsModal}>
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleAddPoints}>
              <div className="modal-body">
                <p style={{ color: '#6b7280', marginBottom: '8px' }}>
                  Add points for <strong>{selectedAgentForPoints.name}</strong>
                </p>
                <p style={{ color: '#6b7280', marginBottom: '20px', fontSize: '0.9rem' }}>
                  Current balance: <strong style={{ color: '#6366f1' }}>{selectedAgentForPoints.pointsBalance || 0} points</strong>
                </p>
                {pointsError && <div className="alert-danger">{pointsError}</div>}
                {pointsSuccess && <div className="alert-success">{pointsSuccess}</div>}
                <div className="form-group">
                  <label className="form-label">Points to Add *</label>
                  <input
                    type="number"
                    value={pointsToAdd}
                    onChange={(e) => setPointsToAdd(e.target.value)}
                    className="form-control"
                    placeholder="Enter amount"
                    min="1"
                    required
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Reason (optional)</label>
                  <input
                    type="text"
                    value={pointsReason}
                    onChange={(e) => setPointsReason(e.target.value)}
                    className="form-control"
                    placeholder="e.g., Bonus for closed deal"
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={closeAddPointsModal}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={isAddingPoints}>
                  {isAddingPoints ? 'Adding...' : 'Add Points'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Referral Modal */}
      {referralModalOpen && selectedAgentForReferral && (
        <div className="modal-overlay" onClick={closeReferralModal}>
          <div className="modal-content modal-content-lg" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Referral Links - {selectedAgentForReferral.name}</h3>
              <button className="modal-close" onClick={closeReferralModal}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              {/* Create New Link Form */}
              <div style={{ 
                background: '#fafafa', 
                borderRadius: '12px', 
                padding: '20px',
                marginBottom: '24px',
                border: '1px solid #f0f0f0'
              }}>
                <h4 style={{ margin: '0 0 16px 0', fontSize: '0.95rem', fontWeight: '600', color: '#111' }}>
                  Create New Referral Link
                </h4>
                {referralError && <div className="alert-danger">{referralError}</div>}
                {referralSuccess && <div className="alert-success">{referralSuccess}</div>}
                <form onSubmit={handleCreateReferralLink}>
                  <div className="form-group">
                    <label className="form-label">Campaign Name</label>
                    <input
                      type="text"
                      value={newReferralData.campaignName}
                      onChange={(e) => setNewReferralData({...newReferralData, campaignName: e.target.value})}
                      className="form-control"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Notes (optional)</label>
                    <input
                      type="text"
                      value={newReferralData.notes}
                      onChange={(e) => setNewReferralData({...newReferralData, notes: e.target.value})}
                      className="form-control"
                      placeholder="Internal notes"
                    />
                  </div>
                  <button type="submit" className="btn btn-primary" disabled={isCreatingReferral}>
                    {isCreatingReferral ? 'Creating...' : 'Create Link'}
                  </button>
                </form>
              </div>

              {/* Existing Links */}
              <h4 style={{ margin: '0 0 12px 0', fontSize: '0.95rem', fontWeight: '600', color: '#111' }}>
                Existing Links ({referralLinks.length})
              </h4>
              {referralLinks.length === 0 ? (
                <div style={{ 
                  textAlign: 'center', 
                  padding: '24px', 
                  background: '#fafafa', 
                  borderRadius: '12px',
                  color: '#9ca3af',
                  border: '1px solid #f0f0f0'
                }}>
                  No referral links yet
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {referralLinks.map(link => (
                    <div key={link.id} style={{
                      background: '#fafafa',
                      borderRadius: '8px',
                      padding: '14px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      border: '1px solid #f0f0f0'
                    }}>
                      <div>
                        <div style={{ fontWeight: '500', color: '#111', marginBottom: '4px' }}>
                          {link.campaignName}
                        </div>
                        <code style={{ fontSize: '0.8rem' }}>{link.linkCode}</code>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                        <div style={{ textAlign: 'center' }}>
                          <div style={{ fontWeight: '600', color: '#111' }}>{link.stats?.clicks || 0}</div>
                          <div style={{ fontSize: '0.7rem', color: '#9ca3af' }}>Clicks</div>
                        </div>
                        <div style={{ textAlign: 'center' }}>
                          <div style={{ fontWeight: '600', color: '#111' }}>{link.stats?.registrations || 0}</div>
                          <div style={{ fontSize: '0.7rem', color: '#9ca3af' }}>Signups</div>
                        </div>
                        <button
                          className="btn btn-secondary btn-sm"
                          onClick={() => copyToClipboard(link.shareableUrl)}
                        >
                          Copy
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={closeReferralModal}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {trackedLinksOpen && (
        <TrackedLinksModal onClose={() => setTrackedLinksOpen(false)} />
      )}

      {/* Message Modal */}
      {showMessageModal && selectedAgentForMessage && (
        <div className="modal-overlay" onClick={closeMessageModal}>
          <div className="modal-content modal-content-lg" onClick={(e) => e.stopPropagation()} style={{ height: '600px' }}>
            <div className="modal-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  background: hasValidProfileImage(selectedAgentForMessage) ? 'transparent' : '#f3f4f6',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  overflow: 'hidden',
                  border: '2px solid #f0f0f0'
                }}>
                  {hasValidProfileImage(selectedAgentForMessage) ? (
                    <img
                      src={selectedAgentForMessage.profileImage}
                      alt={selectedAgentForMessage.name}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  ) : (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                      <circle cx="12" cy="7" r="4"></circle>
                    </svg>
                  )}
                </div>
                <div>
                  <h3 style={{ margin: 0 }}>{selectedAgentForMessage.name}</h3>
                  <span style={{ fontSize: '0.8rem', color: '#6b7280' }}>
                    {selectedAgentForMessage.agentInfo?.agency || 'Agent'}
                  </span>
                </div>
              </div>
              <button className="modal-close" onClick={closeMessageModal}>
                <X size={20} />
              </button>
            </div>
            
            {/* Messages Container */}
            <div style={{
              flex: 1,
              overflowY: 'auto',
              padding: '20px',
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
              background: '#faf9f7'
            }}>
              {messages.length === 0 ? (
                <div style={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#9ca3af'
                }}>
                  No messages yet. Start the conversation!
                </div>
              ) : (
                messages.map((msg) => {
                  const isAdmin = msg.senderRole === 'admin';
                  return (
                    <div
                      key={msg._id}
                      style={{
                        display: 'flex',
                        justifyContent: isAdmin ? 'flex-end' : 'flex-start'
                      }}
                    >
                      <div
                        style={{
                          maxWidth: '70%',
                          padding: '10px 14px',
                          borderRadius: '14px',
                          background: isAdmin ? 'linear-gradient(135deg, #2563eb, #1d4ed8)' : '#fff',
                          color: isAdmin ? '#fff' : '#111',
                          boxShadow: isAdmin ? '0 2px 8px rgba(37, 99, 235, 0.25)' : '0 1px 3px rgba(0,0,0,0.06)',
                          border: isAdmin ? 'none' : '1px solid #f0f0f0',
                          fontSize: '13px',
                          lineHeight: 1.45
                        }}
                      >
                        <div>{msg.content}</div>
                        <div style={{
                          fontSize: '0.7rem',
                          marginTop: '6px',
                          opacity: 0.7
                        }}>
                          {new Date(msg.createdAt).toLocaleTimeString([], {
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Message Input */}
            <div style={{
              padding: '16px 20px',
              borderTop: '1px solid #f0f0f0',
              display: 'flex',
              gap: '12px',
              background: '#fafafa'
            }}>
              <textarea
                value={messageText}
                onChange={(e) => setMessageText(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    sendMessage();
                  }
                }}
                placeholder="Type your message..."
                style={{
                  flex: 1,
                  padding: '12px',
                  borderRadius: '12px',
                  border: '1px solid #e5e7eb',
                  resize: 'none',
                  minHeight: '44px',
                  maxHeight: '100px',
                  fontFamily: 'inherit',
                  fontSize: '0.95rem',
                  background: '#ffffff',
                  color: '#111'
                }}
                rows={1}
              />
              <button
                onClick={sendMessage}
                className="btn btn-primary"
                disabled={isSending || !messageText.trim()}
                style={{ alignSelf: 'flex-end' }}
              >
                {isSending ? 'Sending...' : 'Send'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Agent Modal */}
      {showEditAgentModal && selectedAgentForDetails && (
        // zIndex must beat AgentDetailsModal's overlay (inline 999999), which stays
        // mounted underneath — .modal-overlay's z-index: 1000 loses to it otherwise.
        <div className="modal-overlay" onClick={closeEditAgentModal} style={{ zIndex: 1000000 }}>
          <div className="modal-content modal-content-lg" onClick={(e) => e.stopPropagation()} style={{ maxHeight: '85vh', display: 'flex', flexDirection: 'column' }}>
            <div className="modal-header" style={{ flexShrink: 0 }}>
              <h3>Edit Agent Profile</h3>
              <button className="modal-close" onClick={closeEditAgentModal}>
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleUpdateAgent} style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
              <div className="modal-body" style={{ flex: 1, overflowY: 'auto' }}>
                {editError && <div className="alert-danger">{editError}</div>}
                {editSuccess && <div className="alert-success">{editSuccess}</div>}
                
                {/* Account Info Section */}
                <div style={{ marginBottom: '24px' }}>
                  <h4 style={{ 
                    margin: '0 0 16px 0', 
                    fontSize: '0.9rem', 
                    fontWeight: '600', 
                    color: '#111',
                    paddingBottom: '8px',
                    borderBottom: '1px solid #f0f0f0'
                  }}>
                    Account Information
                  </h4>
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">Full Name *</label>
                      <input
                        type="text"
                        name="name"
                        value={editAgentData.name}
                        onChange={handleEditAgentChange}
                        className="form-control"
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Login Email *</label>
                      <input
                        type="email"
                        name="email"
                        value={editAgentData.email}
                        onChange={handleEditAgentChange}
                        className="form-control"
                        required
                      />
                      <small style={{ color: '#9ca3af', fontSize: '0.7rem', marginTop: '4px', display: 'block' }}>
                        Used for authentication and system notifications
                      </small>
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">Phone Number</label>
                      <input
                        type="tel"
                        name="phone"
                        value={editAgentData.phone}
                        onChange={handleEditAgentChange}
                        className="form-control"
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Display Email (Alias)</label>
                      <input
                        type="email"
                        name="aliasEmail"
                        value={editAgentData.aliasEmail}
                        onChange={handleEditAgentChange}
                        className="form-control"
                        placeholder="Optional - shown to users"
                      />
                      <small style={{ color: '#9ca3af', fontSize: '0.7rem', marginTop: '4px', display: 'block' }}>
                        If set, this email is displayed to users instead of login email
                      </small>
                    </div>
                    <div className="form-group">
                      <label className="form-label">MLS Code</label>
                      <input
                        type="text"
                        name="mlsCode"
                        value={editAgentData.mlsCode}
                        onChange={handleEditAgentChange}
                        className="form-control"
                        placeholder="Optional - per-board MLS-assigned code"
                      />
                      <small style={{ color: '#9ca3af', fontSize: '0.7rem', marginTop: '4px', display: 'block' }}>
                        Only fill if the agent's MLS uses a different identifier than their email/phone
                      </small>
                    </div>
                  </div>
                </div>

                {/* Agent Details Section */}
                <div>
                  <h4 style={{ 
                    margin: '0 0 16px 0', 
                    fontSize: '0.9rem', 
                    fontWeight: '600', 
                    color: '#111',
                    paddingBottom: '8px',
                    borderBottom: '1px solid #f0f0f0'
                  }}>
                    Agent Details
                  </h4>
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">Brokerage / Agency</label>
                      <input
                        type="text"
                        name="agency"
                        value={editAgentData.agency}
                        onChange={handleEditAgentChange}
                        className="form-control"
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">License Number</label>
                      <input
                        type="text"
                        name="licenseNumber"
                        value={editAgentData.licenseNumber}
                        onChange={handleEditAgentChange}
                        className="form-control"
                      />
                    </div>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Subscription Plan</label>
                    <select
                      name="plan"
                      value={editAgentData.plan}
                      onChange={handleEditAgentChange}
                      className="form-control"
                    >
                      <option value="Launch Advantage">Launch Advantage</option>
                      <option value="Launch Premium">Launch Premium</option>
                      <option value="Ground Floor">Ground Floor</option>
                      <option value="Free Trial">Free Trial</option>
                    </select>
                  </div>
                </div>

                {/* Product access. Separate from `plan` above — that's the Pro
                    pricing tier, this is which products exist for the account
                    at all. Also separate from agentStatus: removing 'pro' here
                    means "not a Pro account", whereas cancelling sets
                    agentStatus 'inactive' and keeps the entitlement. */}
                <div className="form-group">
                  <label className="form-label">Product Access</label>
                  <div style={{ display: 'flex', gap: '18px', flexWrap: 'wrap', padding: '9px 0' }}>
                    {[
                      { key: 'pro', label: 'Lauft Pro', hint: 'partner portal, leads, points' },
                      { key: 'studio', label: 'Studio', hint: 'AI listing videos' },
                      { key: 'lease', label: 'Lease', hint: 'not yet live' }
                    ].map(({ key, label, hint }) => (
                      <label
                        key={key}
                        style={{ display: 'flex', alignItems: 'flex-start', gap: '7px', cursor: 'pointer', fontSize: '13px' }}
                      >
                        <input
                          type="checkbox"
                          checked={(editAgentData.entitlements || []).includes(key)}
                          onChange={() => toggleEditEntitlement(key)}
                          style={{ marginTop: '2px', cursor: 'pointer' }}
                        />
                        <span>
                          <span style={{ fontWeight: 600, color: '#111' }}>{label}</span>
                          <span style={{ display: 'block', fontSize: '11px', color: '#9ca3af' }}>{hint}</span>
                        </span>
                      </label>
                    ))}
                  </div>
                  <div style={{ fontSize: '11px', color: '#9ca3af' }}>
                    Removing Pro blocks the partner portal entirely. To pause or cancel
                    billing instead, use the Status column on the roster.
                  </div>
                </div>

                {/* Security & Notifications Section */}
                <div style={{ marginTop: '24px' }}>
                  <h4 style={{
                    margin: '0 0 16px 0',
                    fontSize: '0.9rem',
                    fontWeight: '600',
                    color: '#111',
                    paddingBottom: '8px',
                    borderBottom: '1px solid #f0f0f0'
                  }}>
                    Security & Notifications
                  </h4>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '16px',
                      padding: '16px',
                      backgroundColor: editAgentData.precautionMode ? '#fef3c7' : '#f9fafb',
                      border: editAgentData.precautionMode ? '1px solid #fcd34d' : '1px solid #e5e7eb',
                      borderRadius: '8px',
                      transition: 'all 0.2s'
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        marginBottom: '4px'
                      }}>
                        <span style={{
                          fontSize: '0.9rem',
                          fontWeight: '600',
                          color: '#111'
                        }}>
                          Precaution Mode
                        </span>
                        {editAgentData.precautionMode && (
                          <span style={{
                            fontSize: '0.65rem',
                            fontWeight: '700',
                            color: '#92400e',
                            backgroundColor: '#fde68a',
                            padding: '2px 8px',
                            borderRadius: '999px',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px'
                          }}>
                            Active
                          </span>
                        )}
                      </div>
                      <div style={{
                        fontSize: '0.78rem',
                        color: '#6b7280',
                        lineHeight: '1.45'
                      }}>
                        When enabled, claiming a user will <strong>not</strong> immediately notify them. The agent must vet the user's phone number and manually release their info from the buyer details modal before any email or push is sent.
                      </div>
                    </div>
                    {/* Toggle switch */}
                    <button
                      type="button"
                      onClick={handlePrecautionModeToggle}
                      role="switch"
                      aria-checked={editAgentData.precautionMode}
                      style={{
                        flexShrink: 0,
                        width: '44px',
                        height: '24px',
                        borderRadius: '999px',
                        border: 'none',
                        backgroundColor: editAgentData.precautionMode ? '#f59e0b' : '#d1d5db',
                        position: 'relative',
                        cursor: 'pointer',
                        transition: 'background-color 0.2s',
                        padding: 0,
                        marginTop: '2px'
                      }}
                    >
                      <span
                        style={{
                          position: 'absolute',
                          top: '2px',
                          left: editAgentData.precautionMode ? '22px' : '2px',
                          width: '20px',
                          height: '20px',
                          borderRadius: '50%',
                          backgroundColor: '#ffffff',
                          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.2)',
                          transition: 'left 0.2s'
                        }}
                      />
                    </button>
                  </div>
                  {/* Demo Account toggle */}
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '16px',
                      padding: '16px',
                      marginTop: '12px',
                      backgroundColor: editAgentData.isDemoAccount ? '#dbeafe' : '#f9fafb',
                      border: editAgentData.isDemoAccount ? '1px solid #93c5fd' : '1px solid #e5e7eb',
                      borderRadius: '8px',
                      transition: 'all 0.2s'
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        marginBottom: '4px'
                      }}>
                        <span style={{
                          fontSize: '0.9rem',
                          fontWeight: '600',
                          color: '#111'
                        }}>
                          Demo Account
                        </span>
                        {editAgentData.isDemoAccount && (
                          <span style={{
                            fontSize: '0.65rem',
                            fontWeight: '700',
                            color: '#1e40af',
                            backgroundColor: '#bfdbfe',
                            padding: '2px 8px',
                            borderRadius: '999px',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px'
                          }}>
                            Active
                          </span>
                        )}
                      </div>
                      <div style={{
                        fontSize: '0.78rem',
                        color: '#6b7280',
                        lineHeight: '1.45'
                      }}>
                        When enabled, this agent sees <strong>all</strong> users in their pool on the Available Leads page (claimed and unclaimed), with a "Claimed" badge on taken leads. They can also widen the view to the entire state. <strong>Demo accounts cannot actually claim leads.</strong> Identifying details about which agent claimed a lead are never shown.
                      </div>
                    </div>
                    {/* Toggle switch */}
                    <button
                      type="button"
                      onClick={handleDemoAccountToggle}
                      role="switch"
                      aria-checked={editAgentData.isDemoAccount}
                      style={{
                        flexShrink: 0,
                        width: '44px',
                        height: '24px',
                        borderRadius: '999px',
                        border: 'none',
                        backgroundColor: editAgentData.isDemoAccount ? '#3b82f6' : '#d1d5db',
                        position: 'relative',
                        cursor: 'pointer',
                        transition: 'background-color 0.2s',
                        padding: 0,
                        marginTop: '2px'
                      }}
                    >
                      <span
                        style={{
                          position: 'absolute',
                          top: '2px',
                          left: editAgentData.isDemoAccount ? '22px' : '2px',
                          width: '20px',
                          height: '20px',
                          borderRadius: '50%',
                          backgroundColor: '#ffffff',
                          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.2)',
                          transition: 'left 0.2s'
                        }}
                      />
                    </button>
                  </div>
                </div>
              </div>
              <div className="modal-footer" style={{ flexShrink: 0 }}>
                <button type="button" className="btn btn-secondary" onClick={closeEditAgentModal}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={isUpdatingAgent}>
                  {isUpdatingAgent ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Admin User Modal */}
      {adminUserModalOpen && selectedUserForModal && (
        <AdminUserModal
          user={selectedUserForModal}
          onClose={closeUserDetailsModal}
        />
      )}

      {/* Refund User Modal */}
      {showRefundModal && refundTargetUser && refundAgent && (
        <div className="modal-overlay" onClick={closeRefundModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '480px' }}>
            <div className="modal-header">
              <h3>Refund User</h3>
              <button className="modal-close" onClick={closeRefundModal}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              {refundError && <div className="alert-danger">{refundError}</div>}
              
              <div style={{
                padding: '16px',
                background: '#fafafa',
                borderRadius: '10px',
                marginBottom: '16px',
                border: '1px solid #f0f0f0'
              }}>
                <div style={{ fontWeight: '600', color: '#111', marginBottom: '4px' }}>
                  {refundTargetUser.name}
                </div>
                <div style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: '8px' }}>
                  {refundTargetUser.email}
                </div>
                {refundTargetUser.leadTier && (
                  <span style={{
                    padding: '3px 10px',
                    borderRadius: '6px',
                    fontSize: '0.75rem',
                    fontWeight: '600',
                    textTransform: 'capitalize',
                    background: tierStyle(refundTargetUser.leadTier).bg,
                    color: tierStyle(refundTargetUser.leadTier).fg
                  }}>
                    {refundTargetUser.leadTier} · {
                      getPointCost(refundTargetUser.leadTier, refundTargetUser.createdAt)
                    } pts refund
                  </span>
                )}
              </div>

              <div style={{
                padding: '12px 14px',
                background: 'rgba(234, 179, 8, 0.08)',
                borderRadius: '8px',
                marginBottom: '16px',
                fontSize: '0.83rem',
                color: '#92400e',
                lineHeight: '1.5',
                border: '1px solid rgba(234, 179, 8, 0.15)'
              }}>
                This will return the tier points to <strong>{refundAgent?.name}</strong>, remove this user from the agent, remove them from their pool, and set them back to available.
              </div>

              <div className="form-group">
                <label className="form-label">Refund Reason</label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '8px' }}>
                  {[
                    'User reported unresponsive by agent',
                    'User requested reassignment',
                    'User accidentally claimed',
                    'Incorrect user contact information',
                    'User reported they already have an agent'
                  ].map((option) => (
                    <label
                      key={option}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        padding: '10px 14px',
                        borderRadius: '8px',
                        border: `1px solid ${refundReasonSelected === option ? '#d1d5db' : '#f0f0f0'}`,
                        background: refundReasonSelected === option ? '#fafafa' : 'transparent',
                        cursor: 'pointer',
                        fontSize: '0.88rem',
                        color: '#111',
                        transition: 'all 0.15s ease'
                      }}
                    >
                      <input
                        type="radio"
                        name="refundReason"
                        checked={refundReasonSelected === option}
                        onChange={() => setRefundReasonSelected(option)}
                        style={{ accentColor: '#2563eb' }}
                      />
                      {option}
                    </label>
                  ))}
                  <label
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      padding: '10px 14px',
                      borderRadius: '8px',
                      border: `1px solid ${refundReasonSelected === 'custom' ? '#d1d5db' : '#f0f0f0'}`,
                      background: refundReasonSelected === 'custom' ? '#fafafa' : 'transparent',
                      cursor: 'pointer',
                      fontSize: '0.88rem',
                      color: '#111',
                      transition: 'all 0.15s ease'
                    }}
                  >
                    <input
                      type="radio"
                      name="refundReason"
                      checked={refundReasonSelected === 'custom'}
                      onChange={() => setRefundReasonSelected('custom')}
                      style={{ accentColor: '#2563eb' }}
                    />
                    Other (custom reason)
                  </label>
                </div>
                {refundReasonSelected === 'custom' && (
                  <textarea
                    value={refundReason}
                    onChange={(e) => setRefundReason(e.target.value)}
                    className="form-control"
                    placeholder="Enter custom refund reason..."
                    rows={3}
                    autoFocus
                    style={{
                      resize: 'vertical',
                      minHeight: '72px',
                      fontFamily: 'inherit',
                      marginTop: '4px'
                    }}
                  />
                )}
                <small style={{ color: '#9ca3af', fontSize: '0.75rem', marginTop: '6px', display: 'block' }}>
                  This reason will be sent via webhook and logged in the agent's points history
                </small>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={closeRefundModal}>
                Cancel
              </button>
              <button
                className="btn"
                onClick={confirmRefundUser}
                disabled={refundingUserId === refundTargetUser._id}
                style={{
                  background: '#dc2626',
                  color: '#fff',
                  border: 'none',
                  opacity: refundingUserId === refundTargetUser._id ? 0.6 : 1
                }}
              >
                {refundingUserId === refundTargetUser._id ? 'Refunding...' : 'Confirm Refund'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreateAgentForm;